/**
 * CppCompleteAlgorithms.jsx — C++ STL & Algorithm Patterns Study Guide
 * 13 data structures + 6 algorithm patterns, single-file React + MUI
 */
import { useState, useMemo } from "react";
import {
    Box, Drawer, AppBar, Toolbar, Typography, IconButton,
    List, ListItem, ListItemButton, ListItemIcon, ListItemText,
    Divider, Tabs, Tab, Card, CardContent, Chip, Tooltip,
    Accordion, AccordionSummary, AccordionDetails,
    Button, useMediaQuery, CssBaseline
} from "@mui/material";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import MenuIcon        from "@mui/icons-material/Menu";
import CloseIcon       from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon       from "@mui/icons-material/Check";
import ExpandMoreIcon  from "@mui/icons-material/ExpandMore";
import CodeIcon        from "@mui/icons-material/Code";
import SchoolIcon      from "@mui/icons-material/School";
import FunctionsIcon   from "@mui/icons-material/Functions";
import ListAltIcon     from "@mui/icons-material/ListAlt";
import FilterListIcon  from "@mui/icons-material/FilterList";
import GridOnIcon      from "@mui/icons-material/GridOn";
import LayersIcon      from "@mui/icons-material/Layers";
import LinkIcon        from "@mui/icons-material/Link";
import BookmarkIcon    from "@mui/icons-material/Bookmark";
import ViewStreamIcon  from "@mui/icons-material/ViewStream";
import BarChartIcon    from "@mui/icons-material/BarChart";
import SortIcon        from "@mui/icons-material/Sort";
import StorageIcon     from "@mui/icons-material/Storage";
import TableChartIcon  from "@mui/icons-material/TableChart";
import BlurOnIcon      from "@mui/icons-material/BlurOn";
import QueueIcon       from "@mui/icons-material/Queue";
import RepeatIcon      from "@mui/icons-material/Repeat";

// ── Theme ─────────────────────────────────────────────────────
const APP_COLORS = {
    background: "rgba(16,7,32,0.85)",
    surface:    "rgba(26,14,52,0.95)",
    border:     "rgba(218,165,32,0.25)",
    title:      "#DAA520",
    text:       "#FFFFFF",
    textMuted:  "rgba(255,255,255,0.55)",
    success:    "#56d364",
    warning:    "#ffa657",
    danger:     "#f85149",
    drawerBg:   "rgba(10,4,22,0.98)",
    codeBg:     "rgba(6,2,16,0.98)",
    cppBlue:    "#00599c",
    cppLight:   "#659ad2",
};

const CATEGORY_STYLE_MAP = {
    Insert:   { bg:"#0d3321", color:"#56d364" },
    Access:   { bg:"#0d2236", color:"#79c0ff" },
    Modify:   { bg:"#2d2000", color:"#e3b341" },
    Delete:   { bg:"#2d1a1a", color:"#f85149" },
    Info:     { bg:"#1e1333", color:"#bc8cff" },
    Order:    { bg:"#2a1d00", color:"#ffa657" },
    Convert:  { bg:"#002a2d", color:"#39c5cf" },
    Iterate:  { bg:"#0a1f2e", color:"#67e8f9" },
    Capacity: { bg:"#1a1a2e", color:"#e879f9" },
    Search:   { bg:"#0d2d1a", color:"#4ade80" },
};

const DIFFICULTY_COLOR_MAP = { Easy:"#56d364", Medium:"#ffa657", Hard:"#f85149" };

const buildAppTheme = () => createTheme({
    palette: {
        mode: "dark",
        background: { default: APP_COLORS.background, paper: APP_COLORS.surface },
        primary:    { main: APP_COLORS.title },
        text:       { primary: APP_COLORS.text, secondary: APP_COLORS.textMuted },
    },
    typography: { fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace" },
    components: {
        MuiPaper:  { styleOverrides: { root:  { backgroundImage:"none" } } },
        MuiDrawer: { styleOverrides: { paper: { backgroundImage:"none" } } },
        MuiAppBar: { styleOverrides: { root:  { backgroundImage:"none" } } },
        MuiCard:   { styleOverrides: { root:  { backgroundImage:"none" } } },
    },
});

const DRAWER_WIDTH = 232;
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SECTION 2 — DATA: vector, array, deque
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const VECTOR_DATA = {
    icon:"📦", color:"#f0883e",
    tagline:"Dynamic array — resizable, contiguous memory, O(1) random access",
    badge:"std::vector<T>  (#include <vector>)",
    whenToUse:"Default sequence container. Use when you need random access and dynamic sizing.",
    declare:"std::vector<int> v;\nstd::vector<int> v(10, 0);    // 10 zeros\nstd::vector<int> v = {1,2,3};",
    complexity:{ "push_back":"O(1)*", "pop_back":"O(1)", "operator[]":"O(1)", "insert/erase":"O(n)" },
    note:"*amortised O(1). insert/erase at middle is O(n) due to shifting. Use reserve() to avoid reallocations.",
    methods:[
        { name:"push_back(val)",    cat:"Insert",   desc:"Appends val to the end — amortised O(1). Most common operation", code:'v.push_back(42);\nv.push_back(99);  // [42, 99]' },
        { name:"emplace_back(args)",cat:"Insert",   desc:"Constructs element in-place at end — avoids copy/move, prefer over push_back for objects", code:'v.emplace_back(42);           // int\nvec.emplace_back("Alice", 30); // constructs in place' },
        { name:"insert(pos, val)",  cat:"Insert",   desc:"Inserts at iterator position — O(n) shifts. Use sparingly on large vectors", code:'auto it = v.begin() + 2;\nv.insert(it, 99);   // insert 99 at index 2' },
        { name:"v[i]",              cat:"Access",   desc:"Index access — O(1). NO bounds check. Undefined behaviour if out of range!", code:'v[0];   // first — no check\nv.at(0); // first — throws std::out_of_range' },
        { name:"v.at(i)",           cat:"Access",   desc:"Bounds-checked access — throws std::out_of_range. Safer than operator[]", code:'try { int x = v.at(100); }\ncatch (std::out_of_range& e) { /* handle */ }' },
        { name:"front() / back()",  cat:"Access",   desc:"First / last element — O(1). Undefined if vector is empty!", code:'v.front();  // first element\nv.back();   // last element' },
        { name:"data()",            cat:"Access",   desc:"Returns raw pointer to underlying array — for C API interop", code:'int* ptr = v.data();\n// pass to C functions expecting int*' },
        { name:"pop_back()",        cat:"Delete",   desc:"Removes last element — O(1). Does NOT return the value!", code:'v.pop_back();  // removes last, no return value\n// to get it: int x = v.back(); v.pop_back();' },
        { name:"erase(pos)",        cat:"Delete",   desc:"Erase at iterator — O(n). Invalidates all iterators after position", code:'v.erase(v.begin());         // erase first\nv.erase(v.begin(), v.begin()+3); // erase range' },
        { name:"clear()",           cat:"Delete",   desc:"Removes all elements — size becomes 0, capacity unchanged", code:'v.clear();        // size=0, capacity unchanged\nv.shrink_to_fit(); // release excess memory' },
        { name:"size()",            cat:"Info",     desc:"Returns number of elements — O(1)", code:'v.size();     // element count (size_t)\nv.empty();    // true if size == 0' },
        { name:"capacity()",        cat:"Capacity", desc:"Current allocated storage — may be > size(). Doubles when full", code:'v.capacity();        // allocated slots\nv.reserve(100);      // pre-allocate 100 slots\nv.shrink_to_fit();   // release excess' },
        { name:"resize(n, val)",    cat:"Modify",   desc:"Changes size — fills new slots with val (default 0)", code:'v.resize(10);     // grow/shrink to 10 elements\nv.resize(10, -1); // fill new slots with -1' },
        { name:"assign(n, val)",    cat:"Modify",   desc:"Replaces all content with n copies of val", code:'v.assign(5, 0);   // v = {0,0,0,0,0}\nv.assign({1,2,3}); // v = {1,2,3}' },
        { name:"begin() / end()",   cat:"Iterate",  desc:"Iterator pair — use in range-for or STL algorithms", code:'for (auto it=v.begin(); it!=v.end(); ++it)\n    std::cout << *it;\n// Modern: for (auto& x : v) std::cout << x;' },
        { name:"std::sort()",       cat:"Order",    desc:"Sort with STL algorithm — O(n log n) introsort", code:'#include <algorithm>\nstd::sort(v.begin(), v.end());          // ascending\nstd::sort(v.begin(), v.end(),\n    [](int a, int b){ return a>b; }); // descending' },
        { name:"std::find()",       cat:"Search",   desc:"Linear search — O(n). For sorted vector use lower_bound O(log n)", code:'auto it = std::find(v.begin(), v.end(), 42);\nif (it != v.end()) { /* found */ }\n// sorted: auto it = std::lower_bound(v.begin(),v.end(),42);' },
        { name:"std::reverse()",    cat:"Modify",   desc:"Reverses range in-place — O(n)", code:'std::reverse(v.begin(), v.end());' },
        { name:"swap(other)",       cat:"Modify",   desc:"Swaps contents with another vector — O(1)! (just swaps pointers)", code:'std::vector<int> a={1,2}, b={3,4};\na.swap(b); // O(1) — swaps internal pointers' },
    ],
    questions:[
        {
            title:"Two Sum — find pair summing to target",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 Hash map O(n)", tip:"Store complement in unordered_map. One pass, O(n) time and space.",
                    code:`#include <vector>
#include <unordered_map>

std::vector<int> twoSum(std::vector<int>& nums, int target) {
    std::unordered_map<int,int> seen; // value → index

    for (int i = 0; i < (int)nums.size(); ++i) {
        int complement = target - nums[i];
        auto it = seen.find(complement);
        if (it != seen.end())
            return {it->second, i};
        seen[nums[i]] = i;
    }
    return {}; // no solution
}

// twoSum({2,7,11,15}, 9) → {0,1}` },
                { label:"🟠 Two pointers on sorted (O(n log n))", tip:"Sort first (with index tracking), then converge from both ends.",
                    code:`#include <vector>
#include <algorithm>

std::vector<int> twoSum(std::vector<int>& nums, int target) {
    // Pair each value with its original index
    std::vector<std::pair<int,int>> indexed;
    for (int i=0; i<(int)nums.size(); ++i)
        indexed.push_back({nums[i], i});

    std::sort(indexed.begin(), indexed.end());

    int lo = 0, hi = (int)indexed.size() - 1;
    while (lo < hi) {
        int sum = indexed[lo].first + indexed[hi].first;
        if (sum == target)
            return {indexed[lo].second, indexed[hi].second};
        else if (sum < target) ++lo;
        else --hi;
    }
    return {};
}` },
                { label:"🟢 Brute force O(n²)", tip:"Check every pair. No extra memory. Fine for tiny inputs or as baseline.",
                    code:`#include <vector>

std::vector<int> twoSum(std::vector<int>& nums, int target) {
    int n = nums.size();
    for (int i = 0; i < n; ++i)
        for (int j = i+1; j < n; ++j)
            if (nums[i] + nums[j] == target)
                return {i, j};
    return {};
}
// Time: O(n²)  Space: O(1)
// When to use: guaranteed tiny input, no extra memory allowed` },
            ],
        },
        {
            title:"Maximum subarray (Kadane's Algorithm)",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 Kadane's O(n)", tip:"Track current sum and max sum. Reset current sum when it goes negative.",
                    code:`#include <vector>
#include <algorithm>

int maxSubArray(std::vector<int>& nums) {
    int current = nums[0];
    int best    = nums[0];

    for (int i = 1; i < (int)nums.size(); ++i) {
        // Either extend or restart from current element
        current = std::max(nums[i], current + nums[i]);
        best    = std::max(best, current);
    }
    return best;
}

// maxSubArray({-2,1,-3,4,-1,2,1,-5,4}) → 6  ([4,-1,2,1])` },
                { label:"🟠 Divide and conquer O(n log n)", tip:"Split array, find max crossing midpoint. Classic D&C — O(n log n) but educational.",
                    code:`#include <vector>
#include <algorithm>
#include <climits>

int maxCrossing(std::vector<int>& a, int lo, int mid, int hi) {
    int leftSum = INT_MIN, sum = 0;
    for (int i=mid; i>=lo; --i) { sum+=a[i]; leftSum=std::max(leftSum,sum); }
    int rightSum = INT_MIN; sum=0;
    for (int i=mid+1; i<=hi; ++i) { sum+=a[i]; rightSum=std::max(rightSum,sum); }
    return leftSum + rightSum;
}

int helper(std::vector<int>& a, int lo, int hi) {
    if (lo==hi) return a[lo];
    int mid = (lo+hi)/2;
    return std::max({helper(a,lo,mid),
                     helper(a,mid+1,hi),
                     maxCrossing(a,lo,mid,hi)});
}

int maxSubArray(std::vector<int>& nums) {
    return helper(nums, 0, nums.size()-1);
}` },
                { label:"🟢 DP with index tracking (return subarray)", tip:"Extend Kadane's to record start/end indices of the winning subarray.",
                    code:`#include <vector>

std::vector<int> maxSubArrayWithIndices(std::vector<int>& nums) {
    int best=nums[0], cur=nums[0];
    int start=0, end=0, tempStart=0;

    for (int i=1; i<(int)nums.size(); ++i) {
        if (nums[i] > cur + nums[i]) {
            cur=nums[i]; tempStart=i;
        } else {
            cur+=nums[i];
        }
        if (cur > best) {
            best=cur; start=tempStart; end=i;
        }
    }
    // Return the actual subarray
    return {nums.begin()+start, nums.begin()+end+1};
}

// maxSubArrayWithIndices({-2,1,-3,4,-1,2,1,-5,4}) → {4,-1,2,1}` },
            ],
        },
        {
            title:"Remove duplicates from sorted vector in-place",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 Two-pointer write technique", tip:"Slow pointer = write head. Fast pointer scans. O(n) time, O(1) space.",
                    code:`#include <vector>

int removeDuplicates(std::vector<int>& nums) {
    if (nums.empty()) return 0;
    int write = 0; // slow pointer (write position)

    for (int read = 1; read < (int)nums.size(); ++read) {
        if (nums[read] != nums[write]) {
            ++write;
            nums[write] = nums[read];
        }
    }
    return write + 1; // new length
}

// nums={1,1,2,3,3,4} → nums becomes {1,2,3,4,...}, returns 4` },
                { label:"🟠 std::unique + erase idiom", tip:"The canonical C++ erase-remove idiom — unique compacts, erase trims.",
                    code:`#include <vector>
#include <algorithm>

void removeDuplicates(std::vector<int>& nums) {
    // unique() moves duplicates to end, returns iterator to new end
    auto newEnd = std::unique(nums.begin(), nums.end());
    // erase the duplicate tail
    nums.erase(newEnd, nums.end());
}

// Input must be SORTED for std::unique to work correctly
// For unsorted: sort first, then apply erase-unique` },
                { label:"🟢 Using std::set (preserves uniqueness, loses order)", tip:"Insert all into set → copy back. O(n log n) but simple.",
                    code:`#include <vector>
#include <set>

std::vector<int> removeDuplicatesSet(std::vector<int>& nums) {
    std::set<int> seen(nums.begin(), nums.end());
    return {seen.begin(), seen.end()};
}
// Note: set orders elements — use unordered_set if order irrelevant
// Note: original vector is NOT modified (returns new vector)` },
            ],
        },
    ],
};

const ARRAY_DATA = {
    icon:"📐", color:"#67e8f9",
    tagline:"Fixed-size array on the stack — zero overhead, compile-time size",
    badge:"std::array<T,N>  (#include <array>)",
    whenToUse:"When size is known at compile time. Faster than vector (stack allocation), safer than C arrays.",
    declare:"std::array<int,5> a = {1,2,3,4,5};\nstd::array<int,5> a{};  // zero-initialised",
    complexity:{ "operator[]":"O(1)", "fill":"O(n)", "size":"O(1)", "sort":"O(n log n)" },
    note:"Size is FIXED at compile time — cannot resize. Passed by value copies all N elements; use const ref to avoid copies.",
    methods:[
        { name:"a[i] / a.at(i)",    cat:"Access",   desc:"operator[] — no bounds check; at() throws out_of_range", code:'a[0];      // no check — fast\na.at(0);   // bounds-checked' },
        { name:"front() / back()",  cat:"Access",   desc:"First and last elements — O(1)", code:'a.front();  // a[0]\na.back();   // a[N-1]' },
        { name:"data()",            cat:"Access",   desc:"Pointer to underlying C-array — for C API interop", code:'int* p = a.data();\nmemcpy(p, src, a.size()*sizeof(int));' },
        { name:"fill(val)",         cat:"Modify",   desc:"Sets ALL elements to val — O(n)", code:'a.fill(0);   // all zeros\na.fill(-1);  // all -1' },
        { name:"swap(other)",       cat:"Modify",   desc:"Swaps contents with another std::array of same type/size — O(n)", code:'std::array<int,3> x={1,2,3}, y={4,5,6};\nx.swap(y); // NOTE: O(n) unlike vector swap' },
        { name:"size()",            cat:"Info",     desc:"Returns N — always known at compile time (constexpr)", code:'constexpr size_t n = a.size(); // compile-time constant!\na.empty();  // true only if N==0' },
        { name:"begin() / end()",   cat:"Iterate",  desc:"Iterators for STL algorithm compatibility", code:'for (auto& x : a) std::cout << x;\nstd::sort(a.begin(), a.end());' },
        { name:"std::get<I>(a)",    cat:"Access",   desc:"Compile-time index access — bounds-checked at compile time!", code:'std::get<0>(a);  // first element\n// std::get<99>(a); // COMPILE ERROR if 99 >= N' },
    ],
    questions:[
        {
            title:"Rotate array by k positions",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 Three reverses in-place", tip:"Reverse whole → reverse first k → reverse rest. O(n) time, O(1) space.",
                    code:`#include <array>
#include <algorithm>

template<size_t N>
void rotateRight(std::array<int,N>& a, int k) {
    k = k % N;
    if (k == 0) return;
    // 1. reverse whole
    std::reverse(a.begin(), a.end());
    // 2. reverse first k
    std::reverse(a.begin(), a.begin() + k);
    // 3. reverse the rest
    std::reverse(a.begin() + k, a.end());
}

// {1,2,3,4,5}, k=2 → {4,5,1,2,3}` },
                { label:"🟠 Extra array copy", tip:"Copy into shifted positions. Simpler logic, O(n) extra space.",
                    code:`#include <array>

template<size_t N>
std::array<int,N> rotateRight(std::array<int,N> a, int k) {
    k = ((k % N) + N) % N; // handle negative k
    std::array<int,N> result;
    for (int i = 0; i < N; ++i)
        result[(i + k) % N] = a[i];
    return result;
}` },
                { label:"🟢 std::rotate from <algorithm>", tip:"std::rotate(first, new_first, last) — built-in left rotation.",
                    code:`#include <array>
#include <algorithm>

template<size_t N>
void rotateLeft(std::array<int,N>& a, int k) {
    k = k % N;
    // std::rotate does LEFT rotation: new_first becomes a.begin()+k
    std::rotate(a.begin(), a.begin() + k, a.end());
}

// {1,2,3,4,5}, k=2 → rotateLeft → {3,4,5,1,2}
// For right rotate: rotateLeft(a, N - k)` },
            ],
        },
        {
            title:"Find max and min in array simultaneously",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 std::minmax_element — one pass", tip:"Returns pair of iterators to min and max in a single O(n) pass.",
                    code:`#include <array>
#include <algorithm>

template<size_t N>
std::pair<int,int> minMax(const std::array<int,N>& a) {
    auto [minIt, maxIt] = std::minmax_element(a.begin(), a.end());
    return {*minIt, *maxIt};
}

// 3n/2 - 2 comparisons — more efficient than two separate passes` },
                { label:"🟠 Tournament method (3n/2 comparisons)", tip:"Compare pairs first, then track running min/max. Minimal comparisons.",
                    code:`#include <array>
#include <algorithm>

template<size_t N>
std::pair<int,int> minMax(const std::array<int,N>& a) {
    int mn = a[0], mx = a[0];
    int i = (N % 2 == 0) ? 0 : 1;
    for (; i+1 < (int)N; i += 2) {
        int lo = std::min(a[i], a[i+1]);
        int hi = std::max(a[i], a[i+1]);
        mn = std::min(mn, lo);
        mx = std::max(mx, hi);
    }
    return {mn, mx};
}` },
                { label:"🟢 Structured bindings with std::minmax", tip:"std::minmax on two values returns {smaller, larger} — handy for pairs.",
                    code:`#include <array>
#include <algorithm>
#include <numeric>

// Single-pass using accumulate (functional style)
template<size_t N>
void arrayStats(const std::array<int,N>& a) {
    auto [mn, mx] = std::minmax_element(a.begin(), a.end());
    auto sum = std::accumulate(a.begin(), a.end(), 0LL);
    double avg = static_cast<double>(sum) / N;
    // C++17 structured bindings make unpacking clean
    std::cout << "Min:" << *mn << " Max:" << *mx
              << " Sum:" << sum << " Avg:" << avg;
}` },
            ],
        },
        {
            title:"Matrix multiplication with 2D array",
            difficulty:"Hard",
            solutions:[
                { label:"🔵 Classic triple loop", tip:"i-j-k loop order. Cache-friendly access pattern matters — k innermost is best.",
                    code:`#include <array>

template<size_t N>
using Matrix = std::array<std::array<int,N>,N>;

template<size_t N>
Matrix<N> multiply(const Matrix<N>& A, const Matrix<N>& B) {
    Matrix<N> C{};  // zero-initialise
    for (size_t i=0; i<N; ++i)
        for (size_t k=0; k<N; ++k)       // k before j = cache friendly!
            for (size_t j=0; j<N; ++j)
                C[i][j] += A[i][k] * B[k][j];
    return C;
}
// Note: i-k-j ordering is 5-10x faster than i-j-k
// because B[k][j] accesses B row-by-row (cache hits)` },
                { label:"🟠 Transpose then dot product", tip:"Transposing B first makes B[j] a contiguous row — better cache locality.",
                    code:`#include <array>

template<size_t N>
using Matrix = std::array<std::array<int,N>,N>;

template<size_t N>
Matrix<N> transpose(const Matrix<N>& M) {
    Matrix<N> T{};
    for (size_t i=0; i<N; ++i)
        for (size_t j=0; j<N; ++j)
            T[j][i] = M[i][j];
    return T;
}

template<size_t N>
Matrix<N> multiply(const Matrix<N>& A, const Matrix<N>& B) {
    auto BT = transpose(B);
    Matrix<N> C{};
    for (size_t i=0; i<N; ++i)
        for (size_t j=0; j<N; ++j)
            for (size_t k=0; k<N; ++k)
                C[i][j] += A[i][k] * BT[j][k]; // both row accesses!
    return C;
}` },
                { label:"🟢 Identity and diagonal checks first", tip:"Detect identity/diagonal matrices and short-circuit for O(n²) or O(n) speedup.",
                    code:`#include <array>

template<size_t N>
using Matrix = std::array<std::array<int,N>,N>;

template<size_t N>
bool isIdentity(const Matrix<N>& M) {
    for (size_t i=0; i<N; ++i)
        for (size_t j=0; j<N; ++j)
            if (M[i][j] != (i==j ? 1 : 0)) return false;
    return true;
}

template<size_t N>
Matrix<N> multiply(const Matrix<N>& A, const Matrix<N>& B) {
    if (isIdentity(A)) return B;  // A*I = B
    if (isIdentity(B)) return A;  // A*I = A
    // ... fall through to full multiply
    Matrix<N> C{};
    for (size_t i=0;i<N;++i) for (size_t k=0;k<N;++k) for (size_t j=0;j<N;++j)
        C[i][j]+=A[i][k]*B[k][j];
    return C;
}` },
            ],
        },
    ],
};

const DEQUE_DATA = {
    icon:"🔗", color:"#e879f9",
    tagline:"Double-ended queue — O(1) push/pop at BOTH front and back",
    badge:"std::deque<T>  (#include <deque>)",
    whenToUse:"Queue with front access. Sliding window. Better than vector when pushing to front often.",
    declare:"std::deque<int> dq;\nstd::deque<int> dq(5, 0); // 5 zeros",
    complexity:{ "push_back":"O(1)", "push_front":"O(1)", "pop_front":"O(1)", "operator[]":"O(1)" },
    note:"Like vector but with efficient front operations. Stored as chunks — not fully contiguous. Slightly slower than vector for pure back operations.",
    methods:[
        { name:"push_back(val)",    cat:"Insert",  desc:"Appends to back — O(1) amortised", code:'dq.push_back(10);\ndq.push_back(20);' },
        { name:"push_front(val)",   cat:"Insert",  desc:"Prepends to front — O(1). This is deque's advantage over vector!", code:'dq.push_front(0);  // O(1) — impossible efficiently in vector' },
        { name:"pop_back()",        cat:"Delete",  desc:"Removes last — O(1)", code:'dq.pop_back();' },
        { name:"pop_front()",       cat:"Delete",  desc:"Removes first — O(1). This makes deque perfect for queues", code:'dq.pop_front();  // O(1) — use for queue dequeue!' },
        { name:"dq[i] / dq.at(i)",  cat:"Access",  desc:"Random access — O(1) (slightly slower than vector due to chunked storage)", code:'dq[0];     // front\ndq.back(); // last' },
        { name:"front() / back()",  cat:"Access",  desc:"First/last element — O(1)", code:'dq.front();\ndq.back();' },
        { name:"insert(pos, val)",  cat:"Insert",  desc:"Insert at iterator — O(n)", code:'dq.insert(dq.begin()+2, 99);' },
        { name:"erase(pos)",        cat:"Delete",  desc:"Erase at iterator — O(n)", code:'dq.erase(dq.begin());' },
        { name:"size() / empty()",  cat:"Info",    desc:"Element count / emptiness check — O(1)", code:'dq.size();\ndq.empty();' },
    ],
    questions:[
        {
            title:"Sliding window maximum",
            difficulty:"Hard",
            solutions:[
                { label:"🔵 Monotonic deque O(n)", tip:"Store indices in decreasing-value order. Front = max of current window.",
                    code:`#include <deque>
#include <vector>

std::vector<int> maxSlidingWindow(std::vector<int>& nums, int k) {
    std::deque<int> dq; // stores INDICES, kept in dec. value order
    std::vector<int> result;

    for (int i = 0; i < (int)nums.size(); ++i) {
        // Remove indices outside current window [i-k+1, i]
        while (!dq.empty() && dq.front() < i - k + 1)
            dq.pop_front();

        // Remove indices with smaller values (they can't be max)
        while (!dq.empty() && nums[dq.back()] < nums[i])
            dq.pop_back();

        dq.push_back(i);
        if (i >= k - 1)
            result.push_back(nums[dq.front()]); // front = max
    }
    return result;
}
// {1,3,-1,-3,5,3,6,7}, k=3 → {3,3,5,5,6,7}` },
                { label:"🟠 Segment tree approach O(n log n)", tip:"Build segment tree for range max queries. More setup but flexible.",
                    code:`#include <vector>

class SegTree {
    std::vector<int> t;
    int n;
public:
    SegTree(std::vector<int>& a) : n(a.size()), t(4*a.size()) { build(a,1,0,n-1); }
    void build(std::vector<int>& a, int v, int l, int r) {
        if (l==r) { t[v]=a[l]; return; }
        int m=(l+r)/2;
        build(a,2*v,l,m); build(a,2*v+1,m+1,r);
        t[v]=std::max(t[2*v],t[2*v+1]);
    }
    int query(int v,int l,int r,int ql,int qr) {
        if (qr<l||r<ql) return INT_MIN;
        if (ql<=l&&r<=qr) return t[v];
        int m=(l+r)/2;
        return std::max(query(2*v,l,m,ql,qr),query(2*v+1,m+1,r,ql,qr));
    }
    int query(int l, int r) { return query(1,0,n-1,l,r); }
};` },
                { label:"🟢 Brute force O(nk) for small k", tip:"For small k relative to n, simple loop is readable and fast enough.",
                    code:`#include <vector>
#include <algorithm>

std::vector<int> maxSlidingWindowBrute(std::vector<int>& nums, int k) {
    std::vector<int> result;
    int n = nums.size();
    for (int i = 0; i <= n - k; ++i) {
        int mx = *std::max_element(nums.begin()+i, nums.begin()+i+k);
        result.push_back(mx);
    }
    return result;
}
// O(nk) time — acceptable when k is small (< 50)` },
            ],
        },
        {
            title:"Implement a queue using deque",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 Wrap std::deque as Queue", tip:"push_back to enqueue, pop_front to dequeue — both O(1).",
                    code:`#include <deque>
#include <stdexcept>

template<typename T>
class Queue {
    std::deque<T> data;
public:
    void enqueue(T val) { data.push_back(val); }
    T dequeue() {
        if (data.empty()) throw std::underflow_error("Queue empty");
        T front = data.front();
        data.pop_front();
        return front;
    }
    T peek() const { return data.front(); }
    bool empty() const { return data.empty(); }
    size_t size() const { return data.size(); }
};` },
                { label:"🟠 std::queue<T> — built-in adapter", tip:"std::queue wraps deque by default. Use this in real code.",
                    code:`#include <queue>

// std::queue is already implemented using std::deque!
std::queue<int> q;
q.push(10);   // enqueue
q.push(20);
q.push(30);

std::cout << q.front(); // 10 — peek
q.pop();                // dequeue (returns void)
std::cout << q.front(); // 20

std::cout << q.size();  // 2
std::cout << q.empty(); // false` },
                { label:"🟢 Circular buffer queue (fixed size)", tip:"Ring buffer: O(1) all ops, no allocations after init.",
                    code:`#include <array>
#include <stdexcept>

template<typename T, size_t Cap>
class RingQueue {
    std::array<T, Cap> buf{};
    size_t head=0, tail=0, count=0;
public:
    void push(T val) {
        if (count==Cap) throw std::overflow_error("full");
        buf[tail] = val;
        tail = (tail+1) % Cap;
        ++count;
    }
    T pop() {
        if (count==0) throw std::underflow_error("empty");
        T val = buf[head];
        head = (head+1) % Cap;
        --count;
        return val;
    }
    bool empty() const { return count==0; }
    size_t size() const { return count; }
};` },
            ],
        },
        {
            title:"First non-repeating character in a stream",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 Deque + frequency map", tip:"Deque tracks order. Prune front when freq > 1. O(n).",
                    code:`#include <deque>
#include <unordered_map>
#include <string>

std::string firstUnique(const std::string& stream) {
    std::deque<char> dq;
    std::unordered_map<char,int> freq;
    std::string result;

    for (char c : stream) {
        freq[c]++;
        dq.push_back(c);
        // Evict duplicates from front
        while (!dq.empty() && freq[dq.front()] > 1)
            dq.pop_front();
        result += dq.empty() ? '#' : dq.front();
    }
    return result;
}
// "aababc" → "aa#bba" ... (first unique at each step)` },
                { label:"🟠 Array of 26 counts + queue", tip:"For lowercase letters only — array freq is faster than hash map.",
                    code:`#include <deque>
#include <string>

char firstNonRepeating(std::string& stream, int idx) {
    static int freq[26]{};
    static std::deque<char> dq;

    char c = stream[idx];
    freq[c-'a']++;
    dq.push_back(c);

    while (!dq.empty() && freq[dq.front()-'a'] > 1)
        dq.pop_front();

    return dq.empty() ? '#' : dq.front();
}` },
                { label:"🟢 Linked list + map (O(1) deletion)", tip:"Doubly-linked list + map enables O(1) removal from middle.",
                    code:`#include <list>
#include <unordered_map>
#include <string>

class UniqueStream {
    std::list<char> lst;
    std::unordered_map<char, std::list<char>::iterator> pos;
    std::unordered_map<char, int> freq;
public:
    void add(char c) {
        freq[c]++;
        if (freq[c] == 1) {
            lst.push_back(c);
            pos[c] = std::prev(lst.end());
        } else if (freq[c] == 2) {
            lst.erase(pos[c]);  // O(1) removal with iterator!
            pos.erase(c);
        }
    }
    char getFirst() { return lst.empty() ? '#' : lst.front(); }
};` },
            ],
        },
    ],
};
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SECTION 3 — DATA: list, forward_list, stack, queue, priority_queue
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const LIST_DATA = {
    icon:"🔄", color:"#79c0ff",
    tagline:"Doubly-linked list — O(1) insert/erase anywhere given iterator",
    badge:"std::list<T>  (#include <list>)",
    whenToUse:"When you need frequent insert/erase in the middle. No random access needed.",
    declare:"std::list<int> lst;\nstd::list<int> lst = {1,2,3,4,5};",
    complexity:{ "push_front":"O(1)", "push_back":"O(1)", "insert":"O(1)*", "erase":"O(1)*" },
    note:"*O(1) given iterator. Finding the position is O(n). No random access — cannot use lst[i].",
    methods:[
        { name:"push_front(val)",   cat:"Insert",  desc:"Prepend — O(1). Advantage over vector", code:'lst.push_front(0);' },
        { name:"push_back(val)",    cat:"Insert",  desc:"Append — O(1)", code:'lst.push_back(99);' },
        { name:"insert(it, val)",   cat:"Insert",  desc:"Insert before iterator — O(1) with valid iterator", code:'auto it = lst.begin();\nstd::advance(it, 2);  // O(n) to find pos\nlst.insert(it, 42);   // O(1) insert' },
        { name:"pop_front()",       cat:"Delete",  desc:"Remove first — O(1)", code:'lst.pop_front();' },
        { name:"pop_back()",        cat:"Delete",  desc:"Remove last — O(1)", code:'lst.pop_back();' },
        { name:"erase(it)",         cat:"Delete",  desc:"Erase at iterator — O(1). Iterator must be valid", code:'auto it = std::find(lst.begin(), lst.end(), 3);\nif (it != lst.end()) lst.erase(it);' },
        { name:"remove(val)",       cat:"Delete",  desc:"Remove ALL elements equal to val — O(n)", code:'lst.remove(42);   // removes all 42s\nlst.remove_if([](int x){ return x < 0; }); // conditional' },
        { name:"sort()",            cat:"Order",   desc:"Sorts list in-place — O(n log n). Cannot use std::sort (no random access)", code:'lst.sort();                           // ascending\nlst.sort(std::greater<int>());        // descending\nlst.sort([](int a,int b){return a>b;});' },
        { name:"merge(other)",      cat:"Modify",  desc:"Merges sorted list into this — O(n). Both must be sorted first", code:'lst.sort(); other.sort();\nlst.merge(other); // other becomes empty' },
        { name:"reverse()",         cat:"Modify",  desc:"Reverses list in-place — O(n)", code:'lst.reverse();' },
        { name:"unique()",          cat:"Modify",  desc:"Removes consecutive duplicates — sort first for full dedup", code:'lst.sort();\nlst.unique(); // removes consecutive dups' },
        { name:"splice(pos, other)",cat:"Modify",  desc:"Moves elements from other into this at pos — O(1)!", code:'lst.splice(lst.end(), other); // move all of other\n// O(1) — no copying, just pointer updates' },
        { name:"size() / empty()",  cat:"Info",    desc:"Count / emptiness — O(1) in C++11+", code:'lst.size();\nlst.empty();' },
    ],
    questions:[
        {
            title:"LRU Cache with list + unordered_map",
            difficulty:"Hard",
            solutions:[
                { label:"🔵 Classic list+map LRU", tip:"List maintains recency order. Map gives O(1) lookup. splice is O(1) move.",
                    code:`#include <list>
#include <unordered_map>

class LRUCache {
    int cap;
    std::list<std::pair<int,int>> order; // {key, value} — front=MRU
    std::unordered_map<int, std::list<std::pair<int,int>>::iterator> mp;

public:
    LRUCache(int capacity) : cap(capacity) {}

    int get(int key) {
        auto it = mp.find(key);
        if (it == mp.end()) return -1;
        // Move to front (most recently used)
        order.splice(order.begin(), order, it->second);
        return it->second->second;
    }

    void put(int key, int val) {
        if (mp.count(key)) {
            order.splice(order.begin(), order, mp[key]);
            mp[key]->second = val;
            return;
        }
        if ((int)order.size() == cap) {
            mp.erase(order.back().first); // evict LRU
            order.pop_back();
        }
        order.push_front({key, val});
        mp[key] = order.begin();
    }
};` },
                { label:"🟠 OrderedMap approach", tip:"Use std::map with timestamp keys. Less memory-efficient but illustrative.",
                    code:`#include <map>
#include <unordered_map>

class LRUCache {
    int cap, time = 0;
    std::map<int,std::pair<int,int>> lruOrder; // time→{key,val}
    std::unordered_map<int,std::map<int,std::pair<int,int>>::iterator> pos;

public:
    LRUCache(int c) : cap(c) {}
    int get(int key) {
        if (!pos.count(key)) return -1;
        auto it = pos[key];
        int val = it->second.second;
        lruOrder.erase(it);
        pos[key] = lruOrder.insert({++time,{key,val}}).first;
        return val;
    }
    void put(int key, int val) {
        if (pos.count(key)) { get(key); pos[key]->second.second=val; return; }
        if ((int)pos.size()==cap) { pos.erase(lruOrder.begin()->second.first); lruOrder.erase(lruOrder.begin()); }
        pos[key] = lruOrder.insert({++time,{key,val}}).first;
    }
};` },
                { label:"🟢 Doubly-linked list from scratch", tip:"Manual DLL gives full control and avoids STL overhead.",
                    code:`struct Node { int key, val; Node *prev, *next; };

class LRUCache {
    int cap;
    std::unordered_map<int,Node*> mp;
    Node *head, *tail; // dummy sentinels

    void remove(Node* n) { n->prev->next=n->next; n->next->prev=n->prev; }
    void insertFront(Node* n) { n->next=head->next; n->prev=head; head->next->prev=n; head->next=n; }

public:
    LRUCache(int c):cap(c){ head=new Node{0,0,nullptr,nullptr}; tail=new Node{0,0,nullptr,nullptr}; head->next=tail; tail->prev=head; }
    int get(int k){ if(!mp.count(k))return -1; remove(mp[k]); insertFront(mp[k]); return mp[k]->val; }
    void put(int k,int v){ if(mp.count(k)){remove(mp[k]);delete mp[k];mp.erase(k);}
        else if((int)mp.size()==cap){auto* lru=tail->prev;remove(lru);mp.erase(lru->key);delete lru;}
        auto* n=new Node{k,v};insertFront(n);mp[k]=n; }
};` },
            ],
        },
        {
            title:"Flatten a multilevel doubly linked list",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 Iterative with stack", tip:"Push next onto stack when child found. Splice child list in-place.",
                    code:`#include <list>
#include <stack>

// Flatten using std::list and explicit stack
// Assumes Node has: val, child, next pointers
struct Node { int val; Node* next; Node* child; };

Node* flatten(Node* head) {
    if (!head) return nullptr;
    std::stack<Node*> st;
    Node* curr = head;

    while (curr) {
        if (curr->child) {
            if (curr->next) st.push(curr->next);
            curr->next = curr->child;
            curr->child = nullptr;
        } else if (!curr->next && !st.empty()) {
            curr->next = st.top();
            st.pop();
        }
        curr = curr->next;
    }
    return head;
}` },
                { label:"🟠 Recursive flatten", tip:"Recurse into child, find tail of child list, connect to next.",
                    code:`Node* flattenHelper(Node* head, Node** tail) {
    Node* curr = head;
    *tail = head;
    while (curr) {
        if (curr->child) {
            Node* childTail;
            Node* flatChild = flattenHelper(curr->child, &childTail);
            curr->child = nullptr;
            childTail->next = curr->next;
            curr->next = flatChild;
            *tail = childTail;
            curr = childTail->next;
        } else {
            *tail = curr;
            curr = curr->next;
        }
    }
    return head;
}
Node* flatten(Node* head) {
    Node* tail;
    return flattenHelper(head, &tail);
}` },
                { label:"🟢 std::list splice-based flatten", tip:"Use list::splice for O(1) in-place list manipulation.",
                    code:`#include <list>

std::list<int> flattenLists(std::vector<std::list<int>>& lists) {
    std::list<int> result;
    for (auto& lst : lists) {
        // splice moves entire list in O(1)
        result.splice(result.end(), lst);
    }
    return result;
}` },
            ],
        },
        {
            title:"Sort list using merge sort",
            difficulty:"Hard",
            solutions:[
                { label:"🔵 Built-in list::sort()", tip:"std::list has its own sort() — use this in production. O(n log n), stable.",
                    code:`#include <list>

std::list<int> lst = {5,1,3,2,4};
lst.sort();  // O(n log n), stable
// {1,2,3,4,5}

// Custom comparator:
lst.sort([](int a, int b){ return a > b; }); // descending
// {5,4,3,2,1}

// Sort by absolute value:
lst.sort([](int a, int b){ return std::abs(a) < std::abs(b); });` },
                { label:"🟠 Manual merge sort on list", tip:"Split at midpoint using slow/fast pointers. Merge sorted halves.",
                    code:`#include <list>

std::list<int> mergeSortedLists(std::list<int>& a, std::list<int>& b) {
    std::list<int> result;
    while (!a.empty() && !b.empty()) {
        if (a.front() <= b.front()) { result.splice(result.end(),a,a.begin()); }
        else                        { result.splice(result.end(),b,b.begin()); }
    }
    result.splice(result.end(), a);
    result.splice(result.end(), b);
    return result;
}

void mergeSort(std::list<int>& lst) {
    if (lst.size() <= 1) return;
    std::list<int> half;
    auto mid = std::next(lst.begin(), lst.size()/2);
    half.splice(half.begin(), lst, mid, lst.end());
    mergeSort(lst); mergeSort(half);
    lst = mergeSortedLists(lst, half);
}` },
                { label:"🟢 Copy to vector, sort, copy back", tip:"Simple: dump to vector (random access), sort there, reload. O(n log n).",
                    code:`#include <list>
#include <vector>
#include <algorithm>

void sortList(std::list<int>& lst) {
    std::vector<int> v(lst.begin(), lst.end());
    std::sort(v.begin(), v.end());
    std::copy(v.begin(), v.end(), lst.begin());
}
// Note: extra O(n) space — use when simplicity > memory` },
            ],
        },
    ],
};

const STACK_DATA = {
    icon:"📚", color:"#fb923c",
    tagline:"LIFO adapter — push/pop/top all O(1). Wraps deque by default",
    badge:"std::stack<T>  (#include <stack>)",
    whenToUse:"Undo/redo, DFS, expression parsing, backtracking, call-stack simulation",
    declare:"std::stack<int> st;\nstd::stack<int,std::vector<int>> st; // vector backend",
    complexity:{ push:"O(1)", pop:"O(1)", top:"O(1)", size:"O(1)" },
    note:"std::stack does NOT expose iterators — by design. If you need to iterate, use a deque or vector directly.",
    methods:[
        { name:"push(val)",    cat:"Insert",  desc:"Pushes val onto top — O(1)", code:'st.push(10);\nst.push(20);\nst.push(30); // top is 30' },
        { name:"emplace(args)",cat:"Insert",  desc:"Constructs in-place on top — avoids copy", code:'st.emplace(42);' },
        { name:"pop()",        cat:"Delete",  desc:"Removes top — O(1). Does NOT return value! Get value first with top()", code:'int val = st.top(); // get value first!\nst.pop();           // THEN remove' },
        { name:"top()",        cat:"Access",  desc:"Returns reference to top — O(1). Undefined if empty!", code:'st.top();  // 30 (last pushed)\n// if (st.empty()) return; // always check first!' },
        { name:"empty()",      cat:"Info",    desc:"Returns true if stack has no elements", code:'while (!st.empty()) {\n    int x = st.top(); st.pop();\n}' },
        { name:"size()",       cat:"Info",    desc:"Number of elements — O(1)", code:'st.size(); // 3' },
        { name:"swap(other)",  cat:"Modify",  desc:"Swap two stacks — O(1) (swaps underlying container)", code:'std::stack<int> s1, s2;\ns1.swap(s2);' },
    ],
    questions:[
        {
            title:"Valid parentheses",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 Stack matching", tip:"Push opens, pop and check on closes. Empty at end = valid.",
                    code:`#include <stack>
#include <string>
#include <unordered_map>

bool isValid(const std::string& s) {
    std::stack<char> st;
    std::unordered_map<char,char> pairs = {{')','{' },{']','['},{'}','{'}};
    // Wait — fix:
    std::unordered_map<char,char> match = {{'(',')'},{'{','}'},{'[',']'}};

    for (char c : s) {
        if (c=='(' || c=='{' || c=='[') {
            st.push(c);
        } else {
            if (st.empty()) return false;
            char open = st.top(); st.pop();
            if (match[open] != c) return false;
        }
    }
    return st.empty();
}

// "({[]})" → true
// "({[}])" → false` },
                { label:"🟠 Min Stack — O(1) getMin()", tip:"Parallel min-stack tracks minimum at each level. Pop both in sync.",
                    code:`#include <stack>

class MinStack {
    std::stack<int> data;
    std::stack<int> mins; // parallel — tracks running minimum

public:
    void push(int val) {
        data.push(val);
        int curMin = mins.empty() ? val : std::min(val, mins.top());
        mins.push(curMin);
    }
    void pop() { data.pop(); mins.pop(); }
    int top()    { return data.top(); }
    int getMin() { return mins.top(); } // O(1) always!
};

MinStack ms;
ms.push(5); ms.push(3); ms.push(7); ms.push(2);
ms.getMin(); // 2
ms.pop();
ms.getMin(); // 3` },
                { label:"🟢 Evaluate postfix expression", tip:"Operands push; operators pop two and push result.",
                    code:`#include <stack>
#include <string>
#include <sstream>

int evalPostfix(const std::string& expr) {
    std::stack<int> st;
    std::istringstream iss(expr);
    std::string token;

    while (iss >> token) {
        if (token=="+"||token=="-"||token=="*"||token=="/") {
            int b=st.top(); st.pop();
            int a=st.top(); st.pop();
            if (token=="+") st.push(a+b);
            else if (token=="-") st.push(a-b);
            else if (token=="*") st.push(a*b);
            else st.push(a/b);
        } else {
            st.push(std::stoi(token));
        }
    }
    return st.top();
}
// "3 4 + 2 *" → 14  ((3+4)*2)` },
            ],
        },
        {
            title:"Daily temperatures — next warmer day",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 Monotonic decreasing stack", tip:"Stack stores indices. Pop when warmer day found. result[j] = i - j.",
                    code:`#include <stack>
#include <vector>

std::vector<int> dailyTemperatures(std::vector<int>& T) {
    std::vector<int> result(T.size(), 0);
    std::stack<int> st; // stores indices of unresolved days

    for (int i = 0; i < (int)T.size(); ++i) {
        // Pop all days that are colder than today
        while (!st.empty() && T[st.top()] < T[i]) {
            int j = st.top(); st.pop();
            result[j] = i - j; // days waited
        }
        st.push(i);
    }
    return result;
}
// {73,74,75,71,69,72,76,73} → {1,1,4,2,1,1,0,0}` },
                { label:"🟠 Reverse scan O(n)", tip:"Scan right to left. Use result array itself to jump ahead.",
                    code:`#include <vector>

std::vector<int> dailyTemperatures(std::vector<int>& T) {
    int n = T.size();
    std::vector<int> result(n, 0);

    for (int i = n-2; i >= 0; --i) {
        int j = i + 1;
        while (j < n && T[j] <= T[i]) {
            if (result[j] == 0) { j = n; break; } // no warmer day
            j += result[j]; // jump to next warmer using cached answer!
        }
        if (j < n) result[i] = j - i;
    }
    return result;
}` },
                { label:"🟢 Brute force O(n²)", tip:"Nested loop — simple but slow. Good for tiny inputs.",
                    code:`#include <vector>

std::vector<int> dailyTemperaturesBrute(std::vector<int>& T) {
    int n = T.size();
    std::vector<int> result(n, 0);
    for (int i=0; i<n; ++i)
        for (int j=i+1; j<n; ++j)
            if (T[j] > T[i]) { result[i]=j-i; break; }
    return result;
}` },
            ],
        },
        {
            title:"Largest rectangle in histogram",
            difficulty:"Hard",
            solutions:[
                { label:"🔵 Stack-based O(n)", tip:"Maintain increasing stack of indices. Pop when shorter bar found; compute area.",
                    code:`#include <stack>
#include <vector>

int largestRectangleArea(std::vector<int>& heights) {
    std::stack<int> st;
    heights.push_back(0); // sentinel forces final flush
    int maxArea = 0;

    for (int i = 0; i < (int)heights.size(); ++i) {
        while (!st.empty() && heights[st.top()] > heights[i]) {
            int h = heights[st.top()]; st.pop();
            int w = st.empty() ? i : i - st.top() - 1;
            maxArea = std::max(maxArea, h * w);
        }
        st.push(i);
    }
    heights.pop_back(); // restore
    return maxArea;
}
// {2,1,5,6,2,3} → 10  (bars 5,6)` },
                { label:"🟠 Precompute left/right boundaries", tip:"For each bar find leftmost and rightmost extent. O(n) with two passes.",
                    code:`#include <vector>
#include <stack>

int largestRectangleArea(std::vector<int>& h) {
    int n=h.size();
    std::vector<int> left(n), right(n);
    std::stack<int> st;

    for (int i=0;i<n;++i) {                      // left boundary
        while (!st.empty()&&h[st.top()]>=h[i]) st.pop();
        left[i] = st.empty() ? 0 : st.top()+1;
        st.push(i);
    }
    while (!st.empty()) st.pop();
    for (int i=n-1;i>=0;--i) {                    // right boundary
        while (!st.empty()&&h[st.top()]>=h[i]) st.pop();
        right[i] = st.empty() ? n-1 : st.top()-1;
        st.push(i);
    }

    int maxArea=0;
    for (int i=0;i<n;++i)
        maxArea=std::max(maxArea, h[i]*(right[i]-left[i]+1));
    return maxArea;
}` },
                { label:"🟢 Divide and conquer O(n log n)", tip:"Find min-height bar, split there. Max area spans one side or crosses min.",
                    code:`#include <vector>
#include <algorithm>

int helper(std::vector<int>& h, int l, int r) {
    if (l>r) return 0;
    int minIdx = l;
    for (int i=l;i<=r;++i) if (h[i]<h[minIdx]) minIdx=i;
    return std::max({h[minIdx]*(r-l+1),
                     helper(h,l,minIdx-1),
                     helper(h,minIdx+1,r)});
}
int largestRectangleArea(std::vector<int>& h) { return helper(h,0,h.size()-1); }` },
            ],
        },
    ],
};

const QUEUE_PQUEUE_DATA = {
    icon:"🎡", color:"#f472b6",
    tagline:"FIFO queue + max-heap priority_queue — O(1) front/top access",
    badge:"std::queue<T> / std::priority_queue<T>  (#include <queue>)",
    whenToUse:"queue: BFS, task scheduling. priority_queue: Dijkstra, top-K, heap operations",
    declare:"std::queue<int> q;\nstd::priority_queue<int> maxPQ;          // max-heap\nstd::priority_queue<int,vector<int>,greater<int>> minPQ; // min-heap",
    complexity:{ "push":"O(1)/O(log n)", "pop":"O(1)/O(log n)", "front/top":"O(1)", "empty":"O(1)" },
    note:"queue::pop() returns void — get value with front() THEN pop(). priority_queue::top() gives max (or min for greater<>).",
    methods:[
        { name:"q.push(val)",          cat:"Insert",  desc:"Enqueue to back — O(1) for queue, O(log n) for priority_queue", code:'q.push(10); q.push(20);   // queue\nmaxPQ.push(5); maxPQ.push(3); maxPQ.push(8);' },
        { name:"q.pop()",              cat:"Delete",  desc:"Dequeue front (queue) or remove top (pq) — O(1)/O(log n). Returns void!", code:'q.pop();      // removes front\nmaxPQ.pop();  // removes max, returns void' },
        { name:"q.front()",            cat:"Access",  desc:"Peek front of queue — O(1). Undefined if empty!", code:'q.front();    // 10 (first enqueued)\nq.back();     // 20 (last enqueued)' },
        { name:"maxPQ.top()",          cat:"Access",  desc:"Peek at max element of max-heap — O(1)", code:'maxPQ.top();  // 8 (largest pushed)' },
        { name:"empty() / size()",     cat:"Info",    desc:"Emptiness check / element count — O(1)", code:'q.empty();    // false\nq.size();     // 2' },
        { name:"priority_queue init",  cat:"Insert",  desc:"Build from range in O(n) — faster than n individual pushes", code:'std::vector<int> v={3,1,4,1,5};\nstd::priority_queue<int> pq(v.begin(), v.end()); // O(n) heapify' },
        { name:"Custom comparator",    cat:"Order",   desc:"Min-heap or custom ordering via comparator", code:'// Min-heap:\nstd::priority_queue<int,std::vector<int>,std::greater<int>> minPQ;\n// Custom struct:\nauto cmp=[](std::pair<int,int> a,std::pair<int,int> b){ return a.first>b.first; };\nstd::priority_queue<std::pair<int,int>,std::vector<std::pair<int,int>>,decltype(cmp)> pq(cmp);' },
    ],
    questions:[
        {
            title:"BFS shortest path in unweighted graph",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 Standard BFS with queue", tip:"queue::pop() is O(1) — correct BFS time complexity O(V+E).",
                    code:`#include <queue>
#include <vector>
#include <unordered_map>
#include <unordered_set>

int shortestPath(std::unordered_map<int,std::vector<int>>& graph,
                 int start, int end) {
    if (start == end) return 0;
    std::queue<int> q;
    std::unordered_set<int> visited;
    q.push(start);
    visited.insert(start);
    int dist = 0;

    while (!q.empty()) {
        int sz = q.size(); // snapshot this level
        ++dist;
        for (int i = 0; i < sz; ++i) {
            int node = q.front(); q.pop();
            for (int nb : graph[node]) {
                if (nb == end) return dist;
                if (!visited.count(nb)) {
                    visited.insert(nb);
                    q.push(nb);
                }
            }
        }
    }
    return -1; // unreachable
}` },
                { label:"🟠 Dijkstra with priority_queue", tip:"Min-heap on {dist, node} pairs. Lazy deletion skips outdated entries.",
                    code:`#include <queue>
#include <vector>
#include <climits>

using pii = std::pair<int,int>;

std::vector<int> dijkstra(int n,
    std::vector<std::vector<pii>>& adj, int src) {

    std::vector<int> dist(n, INT_MAX);
    std::priority_queue<pii,std::vector<pii>,std::greater<pii>> pq;

    dist[src] = 0;
    pq.push({0, src});

    while (!pq.empty()) {
        auto [d, u] = pq.top(); pq.pop();
        if (d > dist[u]) continue; // outdated entry
        for (auto [w, v] : adj[u]) {
            if (dist[u] + w < dist[v]) {
                dist[v] = dist[u] + w;
                pq.push({dist[v], v});
            }
        }
    }
    return dist;
}` },
                { label:"🟢 0-1 BFS with deque", tip:"Weight-0 edges go to front (push_front), weight-1 to back (push_back). O(V+E).",
                    code:`#include <deque>
#include <vector>
#include <climits>

std::vector<int> bfs01(int n, std::vector<std::vector<std::pair<int,int>>>& adj, int src) {
    std::vector<int> dist(n, INT_MAX);
    std::deque<int> dq;
    dist[src] = 0;
    dq.push_back(src);

    while (!dq.empty()) {
        int u = dq.front(); dq.pop_front();
        for (auto [v, w] : adj[u]) {
            if (dist[u] + w < dist[v]) {
                dist[v] = dist[u] + w;
                if (w == 0) dq.push_front(v);  // weight-0 edge to front
                else        dq.push_back(v);    // weight-1 edge to back
            }
        }
    }
    return dist;
}` },
            ],
        },
        {
            title:"Find K largest elements",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 Min-heap of size K", tip:"Keep a min-heap of k elements. Pop when size > k. Top = kth largest.",
                    code:`#include <queue>
#include <vector>

std::vector<int> kLargest(std::vector<int>& nums, int k) {
    // Min-heap — smallest of the k largest at top
    std::priority_queue<int,std::vector<int>,std::greater<int>> pq;

    for (int n : nums) {
        pq.push(n);
        if ((int)pq.size() > k)
            pq.pop(); // remove smallest if over capacity
    }

    std::vector<int> result;
    while (!pq.empty()) { result.push_back(pq.top()); pq.pop(); }
    return result; // in ascending order
}
// kLargest({3,2,1,5,6,4}, 2) → {5,6}` },
                { label:"🟠 nth_element O(n) average", tip:"Partial sort — O(n) average with no extra heap. Modifies array in-place.",
                    code:`#include <vector>
#include <algorithm>

std::vector<int> kLargest(std::vector<int>& nums, int k) {
    // nth_element partitions so nums[n-k] is in correct sorted position
    std::nth_element(nums.begin(), nums.begin()+(nums.size()-k), nums.end());
    return {nums.begin()+(nums.size()-k), nums.end()};
}
// O(n) average, O(n²) worst — modifies input!` },
                { label:"🟢 partial_sort O(n log k)", tip:"Sort only first k elements — faster than full sort when k << n.",
                    code:`#include <vector>
#include <algorithm>

std::vector<int> kLargest(std::vector<int>& nums, int k) {
    std::partial_sort(nums.begin(), nums.begin()+k, nums.end(),
                      std::greater<int>()); // top-k in descending order
    return {nums.begin(), nums.begin()+k};
}
// O(n log k) time — good when k is small relative to n` },
            ],
        },
        {
            title:"Merge K sorted lists",
            difficulty:"Hard",
            solutions:[
                { label:"🔵 Priority queue merge", tip:"Push head of each list into min-heap. Pop, push next node. O(N log K).",
                    code:`#include <queue>
#include <vector>

struct ListNode { int val; ListNode* next; };

ListNode* mergeKLists(std::vector<ListNode*>& lists) {
    auto cmp = [](ListNode* a, ListNode* b){ return a->val > b->val; };
    std::priority_queue<ListNode*,std::vector<ListNode*>,decltype(cmp)> pq(cmp);

    for (auto* h : lists)
        if (h) pq.push(h);

    ListNode dummy{0,nullptr}, *tail = &dummy;
    while (!pq.empty()) {
        auto* node = pq.top(); pq.pop();
        tail->next = node;
        tail = node;
        if (node->next) pq.push(node->next);
    }
    return dummy.next;
}` },
                { label:"🟠 Divide and conquer", tip:"Recursively merge pairs of lists. O(N log K) — same complexity, no heap.",
                    code:`ListNode* mergeTwoLists(ListNode* a, ListNode* b) {
    ListNode dummy{0,nullptr}, *tail=&dummy;
    while (a&&b) {
        if (a->val<=b->val){tail->next=a;a=a->next;}
        else{tail->next=b;b=b->next;}
        tail=tail->next;
    }
    tail->next=a?a:b;
    return dummy.next;
}

ListNode* mergeKLists(std::vector<ListNode*>& lists) {
    int n=lists.size();
    for (int step=1;step<n;step*=2)
        for (int i=0;i+step<n;i+=step*2)
            lists[i]=mergeTwoLists(lists[i],lists[i+step]);
    return n?lists[0]:nullptr;
}` },
                { label:"🟢 Collect all, sort, rebuild", tip:"Dump all values to vector, sort, rebuild list. Simple O(N log N).",
                    code:`ListNode* mergeKLists(std::vector<ListNode*>& lists) {
    std::vector<int> vals;
    for (auto* h : lists)
        for (auto* n=h; n; n=n->next)
            vals.push_back(n->val);
    std::sort(vals.begin(), vals.end());
    ListNode dummy{0,nullptr}; auto* cur=&dummy;
    for (int v : vals) { cur->next=new ListNode{v,nullptr}; cur=cur->next; }
    return dummy.next;
}` },
            ],
        },
    ],
};
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SECTION 4 — DATA: set, multiset, unordered_set, map, multimap, unordered_map
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const SET_DATA = {
    icon:"🔷", color:"#58a6ff",
    tagline:"Sorted unique elements — BST-based, O(log n) operations",
    badge:"std::set<T>  (#include <set>)",
    whenToUse:"Maintain sorted unique values. Need lower_bound/upper_bound for range queries.",
    declare:"std::set<int> s;\nstd::set<int> s = {3,1,4,1,5}; // {1,3,4,5} auto-sorted",
    complexity:{ insert:"O(log n)", find:"O(log n)", erase:"O(log n)", begin:"O(1)" },
    note:"Elements are ALWAYS sorted. No duplicates. Use multiset for duplicates, unordered_set for O(1) ops.",
    methods:[
        { name:"insert(val)",         cat:"Insert",  desc:"Inserts val — O(log n). Returns {iterator, bool} pair", code:'auto [it, ok] = s.insert(42); // C++17\nif (ok) { /* newly inserted */ }' },
        { name:"emplace(val)",        cat:"Insert",  desc:"Constructs in-place — preferred for complex types", code:'s.emplace(42);' },
        { name:"find(val)",           cat:"Search",  desc:"Returns iterator to val, or end() if not found — O(log n)", code:'auto it = s.find(42);\nif (it != s.end()) { /* found: *it == 42 */ }' },
        { name:"count(val)",          cat:"Info",    desc:"Returns 0 or 1 for set (set has no duplicates) — O(log n)", code:'s.count(42);  // 0 or 1\n// contains() in C++20:\n// s.contains(42);' },
        { name:"erase(val)",          cat:"Delete",  desc:"Erase by value — O(log n). Erase by iterator is O(1) amortised", code:'s.erase(42);        // by value — O(log n)\ns.erase(it);        // by iterator — O(1)\ns.erase(s.begin(),s.end()); // range' },
        { name:"lower_bound(val)",    cat:"Search",  desc:"First element >= val — O(log n). Key for range queries!", code:'auto it = s.lower_bound(10); // first >= 10\nauto it = s.upper_bound(10); // first > 10\n// all in [lo,hi]: lower_bound(lo) to upper_bound(hi)' },
        { name:"begin() / end()",     cat:"Iterate", desc:"In-order iterators — elements always in sorted order", code:'for (int x : s) std::cout << x; // sorted ascending\nfor (auto it=s.rbegin();it!=s.rend();++it) // reversed' },
        { name:"size() / empty()",    cat:"Info",    desc:"Element count / emptiness — O(1)", code:'s.size();\ns.empty();' },
        { name:"merge(other)",        cat:"Modify",  desc:"C++17: move unique elements from other into this — O(n log n)", code:'s.merge(other); // elements not already in s are moved in' },
    ],
    questions:[
        {
            title:"Find if two sets have common elements",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 Two-pointer on sorted sets", tip:"Both sets are sorted — merge-scan for common elements. O(n) with iterators.",
                    code:`#include <set>
#include <vector>

bool hasCommon(std::set<int>& a, std::set<int>& b) {
    auto ia = a.begin(), ib = b.begin();
    while (ia != a.end() && ib != b.end()) {
        if (*ia == *ib) return true;
        else if (*ia < *ib) ++ia;
        else ++ib;
    }
    return false;
}

std::vector<int> intersection(std::set<int>& a, std::set<int>& b) {
    std::vector<int> result;
    std::set_intersection(a.begin(),a.end(),b.begin(),b.end(),
                          std::back_inserter(result));
    return result;
}` },
                { label:"🟠 count() check for membership", tip:"For each element in smaller set, check if in larger. O(n log n).",
                    code:`#include <set>
#include <vector>

std::vector<int> intersection(std::set<int>& a, std::set<int>& b) {
    std::vector<int> result;
    // Iterate smaller set, look up in larger for efficiency
    const auto& smaller = (a.size()<b.size()) ? a : b;
    const auto& larger  = (a.size()<b.size()) ? b : a;
    for (int x : smaller)
        if (larger.count(x))
            result.push_back(x);
    return result;
}` },
                { label:"🟢 Set difference / union with STL", tip:"set_difference, set_union — all O(n) on sorted sets.",
                    code:`#include <set>
#include <algorithm>
#include <vector>

void setOps(std::set<int>& a, std::set<int>& b) {
    std::vector<int> uni, inter, diff;
    std::set_union(a.begin(),a.end(),b.begin(),b.end(),
                   std::back_inserter(uni));
    std::set_intersection(a.begin(),a.end(),b.begin(),b.end(),
                          std::back_inserter(inter));
    std::set_difference(a.begin(),a.end(),b.begin(),b.end(),
                        std::back_inserter(diff));
}` },
            ],
        },
        {
            title:"Longest consecutive sequence",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 Unordered_set O(n)", tip:"O(1) lookup. Only start a count from sequence beginnings (n-1 not in set).",
                    code:`#include <unordered_set>
#include <vector>

int longestConsecutive(std::vector<int>& nums) {
    std::unordered_set<int> s(nums.begin(), nums.end());
    int best = 0;

    for (int n : s) {
        // Only start counting at sequence beginning
        if (!s.count(n - 1)) {
            int len = 1, curr = n;
            while (s.count(curr + 1)) { ++curr; ++len; }
            best = std::max(best, len);
        }
    }
    return best;
}
// {100,4,200,1,3,2} → 4 (sequence 1,2,3,4)` },
                { label:"🟠 Sorted set approach O(n log n)", tip:"Insert all into std::set (auto-sorted), then scan for consecutive runs.",
                    code:`#include <set>
#include <vector>

int longestConsecutive(std::vector<int>& nums) {
    std::set<int> s(nums.begin(), nums.end()); // O(n log n)
    int best=0, len=1;
    int prev = INT_MIN;
    for (int n : s) {
        if (n == prev+1) { ++len; best=std::max(best,len); }
        else              { len=1; }
        prev=n;
    }
    return best;
}` },
                { label:"🟢 Union-Find approach", tip:"Merge consecutive numbers. Root of each component = start of sequence.",
                    code:`#include <vector>
#include <unordered_map>

class UnionFind {
    std::unordered_map<int,int> parent, size;
public:
    int find(int x){ if (!parent.count(x)) {parent[x]=x;size[x]=1;} if(parent[x]!=x) parent[x]=find(parent[x]); return parent[x]; }
    void unite(int x, int y) {
        int rx=find(x),ry=find(y); if(rx==ry) return;
        if(size[rx]<size[ry]) std::swap(rx,ry);
        parent[ry]=rx; size[rx]+=size[ry];
    }
    int getSize(int x){ return size[find(x)]; }
};

int longestConsecutive(std::vector<int>& nums) {
    UnionFind uf;
    std::unordered_set<int> s(nums.begin(),nums.end());
    for (int n : s) { uf.find(n); if(s.count(n+1)) uf.unite(n,n+1); }
    int best=0;
    for (int n : s) best=std::max(best,uf.getSize(n));
    return best;
}` },
            ],
        },
        {
            title:"Count elements in range [lo, hi]",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 lower_bound + upper_bound", tip:"Both O(log n). Distance between iterators = count in range.",
                    code:`#include <set>

int countInRange(std::set<int>& s, int lo, int hi) {
    auto first = s.lower_bound(lo);  // first >= lo
    auto last  = s.upper_bound(hi);  // first > hi
    return std::distance(first, last); // O(k) — k elements in range
}
// For O(log n) count need an order-statistics tree (not in STL)
// or use policy-based: __gnu_pbds::tree` },
                { label:"🟠 order_statistics_tree (GCC extension)", tip:"Policy-based tree gives O(log n) rank/select. Not standard but very useful.",
                    code:`#include <ext/pb_ds/assoc_container.hpp>
#include <ext/pb_ds/tree_policy.hpp>
using namespace __gnu_pbds;

typedef tree<int,null_type,std::less<int>,
             rb_tree_tag,tree_order_statistics_node_update> OSTree;

// O(log n) rank (number of elements < x):
OSTree t;
t.insert(1); t.insert(3); t.insert(5); t.insert(7);
t.order_of_key(5);   // 2 (elements strictly less than 5)
*t.find_by_order(2); // 5 (0-indexed: element at rank 2)

// Count in [lo, hi]:
int countRange(OSTree& t, int lo, int hi) {
    return t.order_of_key(hi+1) - t.order_of_key(lo);
}` },
                { label:"🟢 Multiset for duplicate-aware range count", tip:"multiset::count is O(log n + k). equal_range gives both bounds.",
                    code:`#include <set>

int countInRange(std::multiset<int>& ms, int lo, int hi) {
    auto first = ms.lower_bound(lo);
    auto last  = ms.upper_bound(hi);
    return std::distance(first, last);
}

// equal_range gets both bounds in one call:
auto [lo_it, hi_it] = ms.equal_range(42);
int count42 = std::distance(lo_it, hi_it);` },
            ],
        },
    ],
};

const MAP_DATA = {
    icon:"🗺️", color:"#7ee787",
    tagline:"Sorted key→value map — BST-based, O(log n) all operations",
    badge:"std::map<K,V>  (#include <map>)",
    whenToUse:"Key→value map with sorted iteration. Need lower_bound/upper_bound on keys.",
    declare:"std::map<std::string,int> m;\nm[\"age\"] = 30;\nstd::map<int,int> freq;",
    complexity:{ insert:"O(log n)", find:"O(log n)", erase:"O(log n)", "[]":"O(log n)" },
    note:"operator[] INSERT-creates missing keys with default value! Use find() or count() to safely check existence.",
    methods:[
        { name:"m[key] = val",         cat:"Insert",  desc:"Inserts key if absent (with default value!) then assigns — O(log n)", code:'m["name"] = "Alice";  // insert or update\n// WARNING: m["x"] creates "x":0 even for just checking!' },
        { name:"m.insert({k,v})",      cat:"Insert",  desc:"Insert pair — returns {iterator, bool}. Fails silently if key exists", code:'m.insert({"name","Alice"});\nauto [it,ok] = m.insert({"age",30}); // C++17' },
        { name:"m.emplace(k,v)",       cat:"Insert",  desc:"Construct key-value in-place — avoids copies", code:'m.emplace("name","Alice");' },
        { name:"m.at(key)",            cat:"Access",  desc:"Throws out_of_range if missing — use for safe read access", code:'m.at("name"); // "Alice"\nm.at("missing"); // throws! Unlike m["missing"]' },
        { name:"m.find(key)",          cat:"Search",  desc:"Returns iterator — O(log n). Check != end() before accessing", code:'auto it = m.find("name");\nif (it != m.end()) it->second; // "Alice"' },
        { name:"m.count(key)",         cat:"Info",    desc:"Returns 0 or 1 — O(log n). Prefer contains() in C++20", code:'if (m.count("name")) { /* exists */ }\n// C++20: if (m.contains("name")) { ... }' },
        { name:"m.erase(key)",         cat:"Delete",  desc:"Erase by key — O(log n). Returns number erased (0 or 1)", code:'m.erase("name");    // by key — O(log n)\nm.erase(it);        // by iterator — O(1) amortised' },
        { name:"m.lower_bound(key)",   cat:"Search",  desc:"First entry with key >= given key — O(log n)", code:'auto it = m.lower_bound("m"); // first key >= "m"\nauto it = m.upper_bound("m"); // first key > "m"' },
        { name:"m.begin() / end()",    cat:"Iterate", desc:"In-order iterators — keys in sorted order", code:'for (auto& [k,v] : m)   // C++17 structured binding\n    std::cout << k << ": " << v;' },
        { name:"insert_or_assign(k,v)",cat:"Modify",  desc:"C++17: inserts if absent, assigns if present — unlike insert()", code:'m.insert_or_assign("age", 31); // always updates' },
        { name:"try_emplace(k,args)",  cat:"Insert",  desc:"C++17: insert only if key absent — args forwarded to value constructor", code:'m.try_emplace("vec", 5, 0); // inserts vec→{0,0,0,0,0} if not present' },
    ],
    questions:[
        {
            title:"Word frequency count",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 map with operator[]", tip:"operator[] auto-initialises missing keys to 0. Then increment.",
                    code:`#include <map>
#include <sstream>
#include <string>
#include <vector>

std::map<std::string,int> wordFrequency(const std::string& text) {
    std::map<std::string,int> freq;
    std::istringstream iss(text);
    std::string word;
    while (iss >> word)
        ++freq[word]; // creates 0 if absent, then increments

    return freq;
}

// Get top-5 by frequency:
std::vector<std::pair<std::string,int>> topN(std::map<std::string,int>& freq, int n) {
    std::vector<std::pair<std::string,int>> v(freq.begin(),freq.end());
    std::partial_sort(v.begin(),v.begin()+n,v.end(),
        [](auto& a,auto& b){ return a.second>b.second; });
    v.resize(n);
    return v;
}` },
                { label:"🟠 unordered_map for O(1) access", tip:"Same logic with unordered_map — O(1) average vs O(log n) for map.",
                    code:`#include <unordered_map>
#include <string>

std::unordered_map<std::string,int> wordFreq(const std::string& text) {
    std::unordered_map<std::string,int> freq;
    size_t start=0, end;
    while ((end=text.find(' ',start)) != std::string::npos) {
        if (end>start) ++freq[text.substr(start,end-start)];
        start=end+1;
    }
    if (start<text.size()) ++freq[text.substr(start)];
    return freq;
}` },
                { label:"🟢 Sort by frequency using multimap", tip:"Invert freq map into multimap<int,string> — auto-sorted by count.",
                    code:`#include <map>
#include <string>

std::multimap<int,std::string> sortByFrequency(
    const std::map<std::string,int>& freq) {

    std::multimap<int,std::string> byFreq;
    for (auto& [word,count] : freq)
        byFreq.insert({count, word});

    // Iterate in descending order:
    for (auto it=byFreq.rbegin(); it!=byFreq.rend(); ++it)
        std::cout << it->second << ": " << it->first << "\n";

    return byFreq;
}` },
            ],
        },
        {
            title:"Group anagrams",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 Sorted-key map", tip:"Sort each word's letters as canonical key. All anagrams share the same key.",
                    code:`#include <map>
#include <vector>
#include <string>
#include <algorithm>

std::vector<std::vector<std::string>> groupAnagrams(
    std::vector<std::string>& words) {

    std::map<std::string,std::vector<std::string>> groups;
    for (auto& w : words) {
        std::string key = w;
        std::sort(key.begin(), key.end()); // canonical form
        groups[key].push_back(w);
    }

    std::vector<std::vector<std::string>> result;
    for (auto& [_,grp] : groups)
        result.push_back(grp);
    return result;
}
// {"eat","tea","tan","ate","nat","bat"}
// → {{"eat","tea","ate"},{"tan","nat"},{"bat"}}` },
                { label:"🟠 Count-array key (26-char frequency)", tip:"Key = char-count array, avoids sort — O(n * 26) vs O(n * L log L).",
                    code:`#include <unordered_map>
#include <vector>
#include <string>

std::vector<std::vector<std::string>> groupAnagrams(std::vector<std::string>& words) {
    std::unordered_map<std::string,std::vector<std::string>> groups;
    for (auto& w : words) {
        std::array<int,26> cnt{};
        for (char c : w) ++cnt[c-'a'];
        std::string key(cnt.begin(), cnt.end()); // 26-byte key
        groups[key].push_back(w);
    }
    std::vector<std::vector<std::string>> res;
    for (auto& [_,g]:groups) res.push_back(g);
    return res;
}` },
                { label:"🟢 Prime product hash", tip:"Assign each letter a prime. Product is unique for each anagram class.",
                    code:`#include <unordered_map>
#include <vector>

std::vector<std::vector<std::string>> groupAnagrams(std::vector<std::string>& words) {
    // Each letter maps to a distinct prime
    static const long long primes[26] = {
        2,3,5,7,11,13,17,19,23,29,31,37,41,43,47,53,59,61,67,71,73,79,83,89,97,101
    };
    std::unordered_map<long long,std::vector<std::string>> groups;
    for (auto& w : words) {
        long long hash = 1;
        for (char c : w) hash *= primes[c-'a'];
        groups[hash].push_back(w);
    }
    // Note: may overflow for very long words — use 128-bit or sorted key instead
    std::vector<std::vector<std::string>> res;
    for (auto& [_,g]:groups) res.push_back(g);
    return res;
}` },
            ],
        },
        {
            title:"Subarray sum equals K using prefix map",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 Prefix sum + unordered_map", tip:"prefix[i] - prefix[j] = k  →  look up prefix[i]-k in map. O(n).",
                    code:`#include <unordered_map>
#include <vector>

int subarraySum(std::vector<int>& nums, int k) {
    std::unordered_map<int,int> prefixCount;
    prefixCount[0] = 1; // empty prefix has sum 0
    int sum = 0, count = 0;

    for (int n : nums) {
        sum += n;
        // Times there's a subarray ending here summing to k
        count += prefixCount[sum - k];
        ++prefixCount[sum];
    }
    return count;
}

// {1,1,1}, k=2 → 2
// {1,2,3}, k=3 → 2  ([1,2] and [3])` },
                { label:"🟠 Ordered map for range prefix sums", tip:"std::map keeps prefix sums sorted. lower_bound gives range count in O(log n).",
                    code:`#include <map>
#include <vector>

// Count subarrays with sum in [lo, hi] using ordered prefix map
int countRangeSum(std::vector<int>& nums, int lo, int hi) {
    std::map<long long,int> prefixCount;
    prefixCount[0] = 1;
    long long sum = 0;
    int count = 0;

    for (int n : nums) {
        sum += n;
        // All prefixes p such that sum-hi <= p <= sum-lo
        auto first = prefixCount.lower_bound(sum-hi);
        auto last  = prefixCount.upper_bound(sum-lo);
        for (auto it=first; it!=last; ++it)
            count += it->second;
        ++prefixCount[sum];
    }
    return count;
}` },
                { label:"🟢 Sliding window (non-negative only)", tip:"Two-pointer works when all nums >= 0 (sum is monotone).",
                    code:`#include <vector>

int subarraySumNonNeg(std::vector<int>& nums, int target) {
    int count=0, sum=0, left=0;
    for (int right=0; right<(int)nums.size(); ++right) {
        sum += nums[right];
        while (sum > target && left <= right) sum -= nums[left++];
        if (sum == target) ++count;
    }
    return count;
}
// Only works when all nums >= 0 (monotone sliding window)` },
            ],
        },
    ],
};

const UNORDERED_MAP_DATA = {
    icon:"⚡", color:"#fbbf24",
    tagline:"Hash map — O(1) average insert/find/erase, no ordering",
    badge:"std::unordered_map<K,V>  (#include <unordered_map>)",
    whenToUse:"Fastest key-value lookup. No need for sorted iteration. Large datasets.",
    declare:"std::unordered_map<string,int> m;\nm.reserve(1000);        // avoid rehashing\nm.max_load_factor(0.25); // fewer collisions",
    complexity:{ insert:"O(1)*", find:"O(1)*", erase:"O(1)*", iterate:"O(n)" },
    note:"*average. Worst case O(n) on hash collisions. Use reserve() and custom hash for performance-critical code.",
    methods:[
        { name:"m[key] = val",          cat:"Insert",  desc:"Insert or update — O(1) avg. Creates default if key absent!", code:'m["name"] = "Alice";\n++m["count"]; // creates 0 then increments' },
        { name:"m.insert({k,v})",       cat:"Insert",  desc:"Insert pair — only if key absent. Returns {it, bool}", code:'auto [it,ok] = m.insert({"name","Alice"});' },
        { name:"m.find(key)",           cat:"Search",  desc:"O(1) avg lookup — returns end() if not found", code:'auto it = m.find("name");\nif (it != m.end()) { it->second; }' },
        { name:"m.count(key)",          cat:"Info",    desc:"Returns 0 or 1 — O(1) avg. contains() in C++20", code:'m.count("name"); // 0 or 1\n// C++20: m.contains("name")' },
        { name:"m.erase(key)",          cat:"Delete",  desc:"Erase by key — O(1) avg", code:'m.erase("name");' },
        { name:"m.reserve(n)",          cat:"Capacity",desc:"Pre-allocate for n elements — avoids rehashing", code:'m.reserve(10000); // avoid rehash as map grows' },
        { name:"m.bucket_count()",      cat:"Info",    desc:"Number of hash buckets — for load factor analysis", code:'m.bucket_count();\nm.load_factor();       // elements/buckets\nm.max_load_factor(0.5); // trigger rehash earlier' },
        { name:"m.begin() / end()",     cat:"Iterate", desc:"Iterates in UNSPECIFIED order — never assume order!", code:'for (auto& [k,v] : m) { /* unordered iteration */ }' },
    ],
    questions:[
        {
            title:"Top K frequent elements",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 unordered_map + min-heap", tip:"Count in O(n), then use min-heap of size k for top-k. O(n log k).",
                    code:`#include <unordered_map>
#include <queue>
#include <vector>

std::vector<int> topKFrequent(std::vector<int>& nums, int k) {
    std::unordered_map<int,int> freq;
    for (int n : nums) ++freq[n]; // O(n) count

    // Min-heap of size k: {frequency, value}
    using pii = std::pair<int,int>;
    std::priority_queue<pii,std::vector<pii>,std::greater<pii>> pq;

    for (auto& [val,cnt] : freq) {
        pq.push({cnt, val});
        if ((int)pq.size() > k) pq.pop(); // keep only top-k
    }

    std::vector<int> result;
    while (!pq.empty()) { result.push_back(pq.top().second); pq.pop(); }
    return result;
}
// {1,1,1,2,2,3}, k=2 → {1,2}` },
                { label:"🟠 Bucket sort approach O(n)", tip:"Frequency can be at most n. Use array of buckets indexed by frequency.",
                    code:`#include <unordered_map>
#include <vector>

std::vector<int> topKFrequent(std::vector<int>& nums, int k) {
    std::unordered_map<int,int> freq;
    for (int n : nums) ++freq[n];

    int n = nums.size();
    std::vector<std::vector<int>> buckets(n+1); // bucket[f] = values with freq f
    for (auto& [val,cnt] : freq)
        buckets[cnt].push_back(val);

    std::vector<int> result;
    for (int f = n; f >= 1 && (int)result.size() < k; --f)
        for (int v : buckets[f])
            result.push_back(v);
    return result;
}` },
                { label:"🟢 nth_element on frequency pairs", tip:"O(n) average using partial sort on value-freq pairs.",
                    code:`#include <unordered_map>
#include <vector>
#include <algorithm>

std::vector<int> topKFrequent(std::vector<int>& nums, int k) {
    std::unordered_map<int,int> freq;
    for (int n : nums) ++freq[n];

    std::vector<std::pair<int,int>> v(freq.begin(), freq.end());
    // nth_element so that v[0..k-1] are the k most frequent
    std::nth_element(v.begin(), v.begin()+k, v.end(),
        [](auto& a, auto& b){ return a.second > b.second; });

    std::vector<int> result;
    for (int i=0; i<k; ++i) result.push_back(v[i].first);
    return result;
}` },
            ],
        },
        {
            title:"Isomorphic strings",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 Two hash maps bidirectional", tip:"Map s→t AND t→s. Inconsistency in either direction means not isomorphic.",
                    code:`#include <unordered_map>
#include <string>

bool isIsomorphic(const std::string& s, const std::string& t) {
    if (s.size() != t.size()) return false;
    std::unordered_map<char,char> s2t, t2s;

    for (int i = 0; i < (int)s.size(); ++i) {
        char a=s[i], b=t[i];
        if (s2t.count(a) && s2t[a]!=b) return false;
        if (t2s.count(b) && t2s[b]!=a) return false;
        s2t[a]=b; t2s[b]=a;
    }
    return true;
}

// isIsomorphic("egg","add") → true
// isIsomorphic("foo","bar") → false` },
                { label:"🟠 Index-encoding approach", tip:"Record first occurrence index of each char. Strings isomorphic if patterns match.",
                    code:`#include <string>
#include <unordered_map>

std::string encode(const std::string& s) {
    std::unordered_map<char,int> firstSeen;
    std::string result;
    for (int i=0; i<(int)s.size(); ++i) {
        if (!firstSeen.count(s[i])) firstSeen[s[i]]=i;
        result += std::to_string(firstSeen[s[i]]) + "#";
    }
    return result;
}

bool isIsomorphic(const std::string& s, const std::string& t) {
    return encode(s) == encode(t);
}` },
                { label:"🟢 Array instead of map (ASCII chars)", tip:"For ASCII input, use array[256] — much faster than hash map.",
                    code:`#include <string>
#include <array>

bool isIsomorphic(const std::string& s, const std::string& t) {
    std::array<int,256> s2t{}, t2s{};
    s2t.fill(-1); t2s.fill(-1);

    for (int i=0; i<(int)s.size(); ++i) {
        int a=s[i], b=t[i];
        if (s2t[a]==-1 && t2s[b]==-1) { s2t[a]=b; t2s[b]=a; }
        else if (s2t[a]!=b || t2s[b]!=a) return false;
    }
    return true;
}
// Array lookup is O(1) with tiny constant vs hash map` },
            ],
        },
        {
            title:"Longest substring without repeating characters",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 Sliding window + unordered_map", tip:"Map stores char → latest index. Jump left pointer directly past repeat.",
                    code:`#include <unordered_map>
#include <string>

int lengthOfLongestSubstring(const std::string& s) {
    std::unordered_map<char,int> lastSeen;
    int maxLen=0, left=0;

    for (int right=0; right<(int)s.size(); ++right) {
        char c = s[right];
        if (lastSeen.count(c) && lastSeen[c] >= left)
            left = lastSeen[c] + 1; // jump past duplicate
        lastSeen[c] = right;
        maxLen = std::max(maxLen, right - left + 1);
    }
    return maxLen;
}

// "abcabcbb" → 3 ("abc")
// "pwwkew"   → 3 ("wke")` },
                { label:"🟠 Array window for ASCII strings", tip:"array<int,128> is faster than unordered_map for ASCII characters.",
                    code:`#include <string>
#include <array>

int lengthOfLongestSubstring(const std::string& s) {
    std::array<int,128> lastSeen;
    lastSeen.fill(-1);
    int maxLen=0, left=0;

    for (int r=0; r<(int)s.size(); ++r) {
        int c = s[r];
        if (lastSeen[c] >= left) left = lastSeen[c]+1;
        lastSeen[c] = r;
        maxLen = std::max(maxLen, r-left+1);
    }
    return maxLen;
}` },
                { label:"🟢 Set-based shrink window", tip:"Set tracks current window. Remove left chars until no duplicate.",
                    code:`#include <unordered_set>
#include <string>

int lengthOfLongestSubstring(const std::string& s) {
    std::unordered_set<char> window;
    int maxLen=0, left=0;

    for (int right=0; right<(int)s.size(); ++right) {
        while (window.count(s[right])) // shrink from left
            window.erase(s[left++]);
        window.insert(s[right]);
        maxLen = std::max(maxLen, right-left+1);
    }
    return maxLen;
}` },
            ],
        },
    ],
};
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SECTION 5 — DATA: Algorithms (6 patterns)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const ALGORITHMS_DATA = {
    icon:"⚡", color:"#60a5fa",
    tagline:"Core algorithm patterns — master these to ace any C++ interview",
    badge:"Algorithm Patterns (C++ STL)",
    whenToUse:"Recognise the pattern shape → apply the template",
    declare:"// No single declaration — each pattern has its own structure",
    complexity:{ "Binary Search":"O(log n)", "Sorting":"O(n log n)", "Two Pointers":"O(n)", "Sliding Window":"O(n)" },
    note:"C++ STL has <algorithm> — use std::sort, std::lower_bound, std::binary_search before writing your own.",
    methods:[],
    questions:[
        {
            title:"Binary Search — lower_bound and upper_bound",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 STL lower_bound / upper_bound", tip:"Always use STL first. lower_bound O(log n) on sorted range.",
                    code:`#include <vector>
#include <algorithm>

// STL binary search functions — always prefer these in C++
std::vector<int> v = {1,2,4,4,5,7,9};

// lower_bound: first element >= target
auto lo = std::lower_bound(v.begin(), v.end(), 4);
// *lo == 4, index = lo - v.begin() = 2

// upper_bound: first element > target
auto hi = std::upper_bound(v.begin(), v.end(), 4);
// *hi == 5, index = hi - v.begin() = 4

// Count of target 4:
int count = hi - lo;  // 2

// std::binary_search: just returns bool
bool found = std::binary_search(v.begin(), v.end(), 7); // true` },
                { label:"🟠 Manual binary search template", tip:"left < right loop with careful mid calculation. Works for 'find minimum valid'.",
                    code:`#include <vector>

// Manual binary search — use when STL doesn't fit the problem shape
int binarySearch(std::vector<int>& nums, int target) {
    int left=0, right=(int)nums.size()-1;
    while (left <= right) {
        int mid = left + (right-left)/2; // avoids overflow vs (l+r)/2
        if      (nums[mid] == target) return mid;
        else if (nums[mid] <  target) left  = mid+1;
        else                          right = mid-1;
    }
    return -1;
}

// Search on answer space — e.g. capacity problem:
int minCapacity(std::vector<int>& weights, int days) {
    int lo=*std::max_element(weights.begin(),weights.end());
    int hi=std::accumulate(weights.begin(),weights.end(),0);
    while (lo<hi) {
        int mid=(lo+hi)/2;
        int d=1, cur=0;
        for (int w:weights){if(cur+w>mid){++d;cur=0;}cur+=w;}
        if (d<=days) hi=mid; else lo=mid+1;
    }
    return lo;
}` },
                { label:"🟢 Binary search on rotated array", tip:"One half always sorted — check which, then decide which to search.",
                    code:`#include <vector>

int searchRotated(std::vector<int>& nums, int target) {
    int lo=0, hi=(int)nums.size()-1;
    while (lo<=hi) {
        int mid=(lo+hi)/2;
        if (nums[mid]==target) return mid;
        // Left half sorted?
        if (nums[lo]<=nums[mid]) {
            if (nums[lo]<=target && target<nums[mid]) hi=mid-1;
            else lo=mid+1;
        } else { // Right half sorted
            if (nums[mid]<target && target<=nums[hi]) lo=mid+1;
            else hi=mid-1;
        }
    }
    return -1;
}
// {4,5,6,7,0,1,2}, target=0 → 4` },
            ],
        },
        {
            title:"Sorting — custom comparators and stable_sort",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 Lambda comparators", tip:"Pass lambda as comparator to sort, priority_queue, or any ordering context.",
                    code:`#include <vector>
#include <algorithm>
#include <string>

std::vector<std::pair<std::string,int>> people = {
    {"Alice",30},{"Bob",25},{"Carol",35},{"Dave",25}
};

// Sort by age ascending, then name ascending on tie
std::sort(people.begin(), people.end(),
    [](const auto& a, const auto& b){
        if (a.second != b.second) return a.second < b.second;
        return a.first < b.first;
    });

// stable_sort preserves original order of equal elements
std::stable_sort(people.begin(), people.end(),
    [](const auto& a, const auto& b){ return a.second < b.second; });

// std::sort with struct:
struct Cmp {
    bool operator()(const std::pair<int,int>& a,
                    const std::pair<int,int>& b) const {
        return a.first != b.first ? a.first < b.first : a.second > b.second;
    }
};
std::sort(people.begin(), people.end(), Cmp{});` },
                { label:"🟠 Counting sort O(n+k)", tip:"For integers in small range. O(n+k) time, O(k) space.",
                    code:`#include <vector>
#include <algorithm>

void countingSort(std::vector<int>& v) {
    if (v.empty()) return;
    int lo = *std::min_element(v.begin(),v.end());
    int hi = *std::max_element(v.begin(),v.end());
    std::vector<int> count(hi-lo+1, 0);
    for (int x : v) ++count[x-lo];
    int idx=0;
    for (int i=0; i<(int)count.size(); ++i)
        while (count[i]--) v[idx++]=i+lo;
}` },
                { label:"🟢 std::nth_element for partial sort", tip:"O(n) average — places kth element in correct position. Faster than full sort.",
                    code:`#include <vector>
#include <algorithm>

int kthSmallest(std::vector<int> nums, int k) {
    // nth_element places k-1 th smallest at nums[k-1]
    std::nth_element(nums.begin(), nums.begin()+k-1, nums.end());
    return nums[k-1];
}

// Median:
double median(std::vector<int> nums) {
    int n=nums.size();
    std::nth_element(nums.begin(), nums.begin()+n/2, nums.end());
    double med = nums[n/2];
    if (n%2==0) {
        auto it = std::max_element(nums.begin(), nums.begin()+n/2);
        med = (med + *it)/2.0;
    }
    return med;
}` },
            ],
        },
        {
            title:"Two Pointers — container with most water",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 Converging two pointers", tip:"Move shorter pointer inward — only way to find larger area. O(n).",
                    code:`#include <vector>
#include <algorithm>

int maxArea(std::vector<int>& height) {
    int lo=0, hi=(int)height.size()-1, best=0;
    while (lo < hi) {
        int area = std::min(height[lo],height[hi]) * (hi-lo);
        best = std::max(best, area);
        // Move the shorter side — it's the limiting factor
        if (height[lo] < height[hi]) ++lo;
        else --hi;
    }
    return best;
}
// {1,8,6,2,5,4,8,3,7} → 49` },
                { label:"🟠 Three sum", tip:"Fix one, two-pointer the rest. Sort first. Skip duplicates carefully.",
                    code:`#include <vector>
#include <algorithm>

std::vector<std::vector<int>> threeSum(std::vector<int> nums) {
    std::sort(nums.begin(), nums.end());
    std::vector<std::vector<int>> result;

    for (int i=0; i<(int)nums.size()-2; ++i) {
        if (i>0 && nums[i]==nums[i-1]) continue; // skip dup fixed
        int lo=i+1, hi=(int)nums.size()-1;
        while (lo<hi) {
            int sum=nums[i]+nums[lo]+nums[hi];
            if (sum==0) {
                result.push_back({nums[i],nums[lo],nums[hi]});
                while(lo<hi&&nums[lo]==nums[lo+1])++lo;
                while(lo<hi&&nums[hi]==nums[hi-1])--hi;
                ++lo; --hi;
            } else if (sum<0) ++lo;
            else --hi;
        }
    }
    return result;
}` },
                { label:"🟢 Dutch national flag (3-way partition)", tip:"Sort array of 0s, 1s, 2s in O(n) one pass. Three-pointer technique.",
                    code:`#include <vector>

void sortColors(std::vector<int>& nums) {
    int lo=0, mid=0, hi=(int)nums.size()-1;

    while (mid<=hi) {
        if      (nums[mid]==0) { std::swap(nums[lo++],nums[mid++]); }
        else if (nums[mid]==1) { ++mid; }
        else                   { std::swap(nums[mid],nums[hi--]); }
        // Note: don't increment mid when swapping with hi!
    }
}
// {2,0,2,1,1,0} → {0,0,1,1,2,2}` },
            ],
        },
        {
            title:"Sliding Window — minimum window substring",
            difficulty:"Hard",
            solutions:[
                { label:"🔵 Variable window with freq arrays", tip:"Expand right, shrink left when valid. Track 'have' vs 'need'. O(n).",
                    code:`#include <string>
#include <unordered_map>

std::string minWindow(const std::string& s, const std::string& t) {
    std::unordered_map<char,int> need, have;
    for (char c : t) ++need[c];
    int formed=0, required=need.size();
    int lo=0, minLen=INT_MAX, minStart=0;

    for (int hi=0; hi<(int)s.size(); ++hi) {
        char c=s[hi];
        ++have[c];
        if (need.count(c)&&have[c]==need[c]) ++formed;

        while (formed==required) {
            if (hi-lo+1 < minLen) { minLen=hi-lo+1; minStart=lo; }
            char l=s[lo++];
            --have[l];
            if (need.count(l)&&have[l]<need[l]) --formed;
        }
    }
    return minLen==INT_MAX ? "" : s.substr(minStart,minLen);
}
// s="ADOBECODEBANC", t="ABC" → "BANC"` },
                { label:"🟠 Fixed window — max sum of k elements", tip:"Classic fixed window: add right, subtract left, track max. O(n).",
                    code:`#include <vector>
#include <numeric>

int maxSumSubarray(std::vector<int>& nums, int k) {
    if ((int)nums.size() < k) return -1;
    int sum = std::accumulate(nums.begin(), nums.begin()+k, 0);
    int best = sum;

    for (int i=k; i<(int)nums.size(); ++i) {
        sum += nums[i] - nums[i-k]; // slide: add right, drop left
        best = std::max(best, sum);
    }
    return best;
}
// {2,1,5,1,3,2}, k=3 → 9  (5+1+3)` },
                { label:"🟢 Count distinct in window (sliding set)", tip:"unordered_map counts; erase when count hits 0. Distinct = map size.",
                    code:`#include <vector>
#include <unordered_map>

// Count subarrays with exactly k distinct integers
int atMostK(std::vector<int>& nums, int k) {
    std::unordered_map<int,int> freq;
    int lo=0, count=0;
    for (int hi=0; hi<(int)nums.size(); ++hi) {
        ++freq[nums[hi]];
        while ((int)freq.size()>k) {
            if (--freq[nums[lo]]==0) freq.erase(nums[lo]);
            ++lo;
        }
        count += hi-lo+1;
    }
    return count;
}

int exactlyK(std::vector<int>& nums, int k) {
    return atMostK(nums,k) - atMostK(nums,k-1);
}` },
            ],
        },
        {
            title:"Dynamic Programming — coin change",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 Bottom-up DP", tip:"dp[i] = min coins to make amount i. Build from 0 to target. O(n*amount).",
                    code:`#include <vector>
#include <algorithm>
#include <climits>

int coinChange(std::vector<int>& coins, int amount) {
    std::vector<int> dp(amount+1, INT_MAX);
    dp[0] = 0;

    for (int i=1; i<=amount; ++i) {
        for (int c : coins) {
            if (c<=i && dp[i-c]!=INT_MAX)
                dp[i] = std::min(dp[i], dp[i-c]+1);
        }
    }
    return dp[amount]==INT_MAX ? -1 : dp[amount];
}

// coinChange({1,5,11}, 15) → 3  (5+5+5)
// coinChange({2}, 3) → -1  (impossible)` },
                { label:"🟠 Top-down memoization", tip:"Recursive with memo array. -1 = unsolved, -2 = impossible.",
                    code:`#include <vector>

int helper(std::vector<int>& coins, int rem, std::vector<int>& memo) {
    if (rem < 0) return -1;
    if (rem == 0) return 0;
    if (memo[rem] != -1) return memo[rem];

    int best = INT_MAX;
    for (int c : coins) {
        int sub = helper(coins, rem-c, memo);
        if (sub != -1) best = std::min(best, sub+1);
    }
    memo[rem] = (best==INT_MAX) ? -2 : best;
    return memo[rem]==-2 ? -1 : memo[rem];
}

int coinChange(std::vector<int>& coins, int amount) {
    std::vector<int> memo(amount+1, -1);
    return helper(coins, amount, memo);
}` },
                { label:"🟢 Greedy (only for canonical coin systems)", tip:"Greedy works for US coins but NOT all systems. Use DP for general case.",
                    code:`#include <vector>
#include <algorithm>

// WARNING: only correct for canonical coin systems (US: 1,5,10,25)
// Use DP for general case!
int coinChangeGreedy(std::vector<int> coins, int amount) {
    std::sort(coins.rbegin(), coins.rend()); // largest first
    int count=0;
    for (int c : coins) {
        count += amount/c;
        amount %= c;
    }
    return amount==0 ? count : -1;
}
// Works: {25,10,5,1}, 41 → 4 (25+10+5+1)
// Fails: {1,3,4}, 6 → greedy gives 3 (4+1+1), DP gives 2 (3+3)` },
            ],
        },
        {
            title:"Recursion & Backtracking — N-Queens",
            difficulty:"Hard",
            solutions:[
                { label:"🔵 Bit-mask backtracking", tip:"Use bitmasks for column/diagonal tracking. O(1) conflict check.",
                    code:`#include <vector>
#include <string>

class NQueens {
    std::vector<std::vector<std::string>> result;
    int n;
public:
    std::vector<std::vector<std::string>> solve(int _n) {
        n=_n;
        std::vector<int> queens(n,-1);
        backtrack(queens,0,0,0,0);
        return result;
    }
private:
    void backtrack(std::vector<int>& q, int row,
                   int cols, int diag1, int diag2) {
        if (row==n) { result.push_back(build(q)); return; }
        int avail = ((1<<n)-1) & ~(cols|diag1|diag2);
        while (avail) {
            int bit=avail&(-avail); avail-=bit;
            int col=__builtin_ctz(bit);
            q[row]=col;
            backtrack(q,row+1,cols|bit,(diag1|bit)<<1,(diag2|bit)>>1);
        }
    }
    std::vector<std::string> build(std::vector<int>& q) {
        std::vector<std::string> board(n,std::string(n,'.'));
        for (int i=0;i<n;++i) board[i][q[i]]='Q';
        return board;
    }
};` },
                { label:"🟠 Classic backtracking with sets", tip:"Three sets track occupied columns, diagonals. Clean and readable.",
                    code:`#include <vector>
#include <string>
#include <unordered_set>

class Solution {
    std::vector<std::vector<std::string>> res;
    std::unordered_set<int> cols, d1, d2; // column, diag, anti-diag

    void solve(std::vector<std::string>& board, int row) {
        int n=board.size();
        if (row==n) { res.push_back(board); return; }
        for (int c=0; c<n; ++c) {
            if (cols.count(c)||d1.count(row-c)||d2.count(row+c)) continue;
            board[row][c]='Q';
            cols.insert(c); d1.insert(row-c); d2.insert(row+c);
            solve(board,row+1);
            board[row][c]='.';
            cols.erase(c); d1.erase(row-c); d2.erase(row+c);
        }
    }
public:
    std::vector<std::vector<std::string>> solveNQueens(int n) {
        std::vector<std::string> board(n,std::string(n,'.'));
        solve(board,0); return res;
    }
};` },
                { label:"🟢 Count solutions only (no board building)", tip:"When only the count matters, skip board construction entirely.",
                    code:`#include <vector>

int totalNQueens(int n) {
    std::vector<bool> col(n,false), d1(2*n,false), d2(2*n,false);
    int count=0;

    std::function<void(int)> bt = [&](int row) {
        if (row==n) { ++count; return; }
        for (int c=0; c<n; ++c) {
            if (col[c]||d1[row-c+n]||d2[row+c]) continue;
            col[c]=d1[row-c+n]=d2[row+c]=true;
            bt(row+1);
            col[c]=d1[row-c+n]=d2[row+c]=false;
        }
    };
    bt(0);
    return count;
}
// totalNQueens(4) → 2
// totalNQueens(8) → 92` },
            ],
        },
    ],
};

// ── ALL_TOPICS registry ────────────────────────────────────────
const ALL_TOPICS = [
    { id:"vector",        label:"vector",          data:VECTOR_DATA,         icon:<ListAltIcon /> },
    { id:"array",         label:"array",           data:ARRAY_DATA,          icon:<TableChartIcon /> },
    { id:"deque",         label:"deque",           data:DEQUE_DATA,          icon:<LinkIcon /> },
    { id:"list",          label:"list",            data:LIST_DATA,           icon:<ViewStreamIcon /> },
    { id:"stack",         label:"stack",           data:STACK_DATA,          icon:<LayersIcon /> },
    { id:"queue",         label:"queue/pq",        data:QUEUE_PQUEUE_DATA,   icon:<QueueIcon /> },
    { id:"set",           label:"set",             data:SET_DATA,            icon:<FilterListIcon /> },
    { id:"map",           label:"map",             data:MAP_DATA,            icon:<GridOnIcon /> },
    { id:"unordered_map", label:"unordered_map",   data:UNORDERED_MAP_DATA,  icon:<BlurOnIcon /> },
    { id:"algorithms",    label:"Algorithms",      data:ALGORITHMS_DATA,     icon:<FunctionsIcon /> },
];
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SECTION 3 — COMPONENTS
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const ComplexityBadge = ({ operation, value }) => {
    const getColor = (v) => {
        if (v.includes("1)") || v.includes("1)*")) return APP_COLORS.success;
        if (v.includes("log"))                      return APP_COLORS.warning;
        if (v.includes("varies") || v.includes("N/A")) return APP_COLORS.textMuted;
        return APP_COLORS.danger;
    };
    return (
        <Box sx={{ textAlign:"center", border:`1px solid ${APP_COLORS.border}`, borderRadius:1.5, px:1.5, py:0.5, minWidth:64, bgcolor:"rgba(0,0,0,0.3)" }}>
            <Typography variant="caption" sx={{ color:APP_COLORS.textMuted, display:"block", fontSize:9, textTransform:"uppercase" }}>{operation}</Typography>
            <Typography variant="caption" sx={{ color:getColor(value), fontWeight:700, fontSize:11 }}>{value}</Typography>
        </Box>
    );
};

const CategoryChip = ({ category }) => {
    const style = CATEGORY_STYLE_MAP[category] || { bg:"#1e2d3d", color:"#8b949e" };
    return (
        <Chip label={category} size="small" sx={{ bgcolor:style.bg, color:style.color, fontWeight:700, fontSize:9, height:20, "& .MuiChip-label":{ px:1 } }} />
    );
};

const CopyButton = ({ textToCopy, copyId, copiedId, onCopy }) => {
    const isCopied = copiedId === copyId;
    return (
        <Tooltip title={isCopied ? "Copied!" : "Copy code"}>
            <IconButton size="small"
                        onClick={() => { navigator.clipboard.writeText(textToCopy); onCopy(copyId); }}
                        sx={{ color:isCopied ? APP_COLORS.success : APP_COLORS.textMuted, "&:hover":{ color:APP_COLORS.title }, transition:"color 0.2s" }}>
                {isCopied ? <CheckIcon sx={{ fontSize:14 }} /> : <ContentCopyIcon sx={{ fontSize:14 }} />}
            </IconButton>
        </Tooltip>
    );
};

const CodeBlock = ({ code, copyId, copiedId, onCopy }) => (
    <Box sx={{ position:"relative", mt:1 }}>
        <Box sx={{ position:"absolute", top:4, right:4, zIndex:1 }}>
            <CopyButton textToCopy={code} copyId={copyId} copiedId={copiedId} onCopy={onCopy} />
        </Box>
        <Box component="pre" sx={{
            bgcolor:APP_COLORS.codeBg, border:`1px solid ${APP_COLORS.border}`, borderRadius:1.5,
            p:"12px 36px 12px 14px", fontSize:11, lineHeight:1.85, color:APP_COLORS.text,
            overflowX:"auto", m:0, whiteSpace:"pre",
            fontFamily:"'JetBrains Mono','Fira Code','Courier New',monospace",
        }}>
            {code}
        </Box>
    </Box>
);

const MethodCard = ({ method, color, cardIndex, copiedId, onCopy }) => (
    <Card sx={{ bgcolor:APP_COLORS.surface, border:`1px solid ${APP_COLORS.border}`, borderRadius:2, transition:"border-color 0.2s, transform 0.15s", "&:hover":{ borderColor:color, transform:"translateY(-1px)" } }}>
        <CardContent sx={{ p:"12px !important" }}>
            <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center", mb:0.75 }}>
                <Typography variant="caption" sx={{ color, fontWeight:700, fontSize:12, fontFamily:"monospace" }}>{method.name}</Typography>
                <CategoryChip category={method.cat} />
            </Box>
            <Typography variant="caption" sx={{ color:APP_COLORS.textMuted, display:"block", mb:0.75, lineHeight:1.5, fontSize:10.5 }}>{method.desc}</Typography>
            <CodeBlock code={method.code} copyId={`method-${cardIndex}`} copiedId={copiedId} onCopy={onCopy} />
        </CardContent>
    </Card>
);

const SolutionPanel = ({ question, color, copiedId, onCopy }) => {
    const [activeSolution, setActiveSolution] = useState(0);
    const diffColor = DIFFICULTY_COLOR_MAP[question.difficulty] || APP_COLORS.textMuted;
    const current   = question.solutions[activeSolution];
    return (
        <Card sx={{ bgcolor:APP_COLORS.surface, border:`1px solid ${color}44`, borderRadius:2, overflow:"hidden" }}>
            <CardContent sx={{ p:"0 !important" }}>
                <Box sx={{ display:"flex", alignItems:"center", gap:1.5, p:"12px 16px", bgcolor:"rgba(0,0,0,0.3)", borderBottom:`1px solid ${APP_COLORS.border}` }}>
                    <CodeIcon sx={{ color, fontSize:18 }} />
                    <Box>
                        <Typography sx={{ fontWeight:700, fontSize:13, color:APP_COLORS.text }}>{question.title}</Typography>
                        <Typography sx={{ fontSize:10, color:diffColor }}>● {question.difficulty} · 3 solutions</Typography>
                    </Box>
                </Box>
                <Tabs value={activeSolution} onChange={(_,v) => setActiveSolution(v)} sx={{ bgcolor:"rgba(0,0,0,0.2)", borderBottom:`1px solid ${APP_COLORS.border}`, minHeight:38, "& .MuiTab-root":{ minHeight:38, fontSize:10, fontWeight:600, color:APP_COLORS.textMuted }, "& .Mui-selected":{ color:`${color} !important` }, "& .MuiTabs-indicator":{ bgcolor:color } }}>
                    {question.solutions.map((sol,i) => <Tab key={i} label={sol.label} />)}
                </Tabs>
                <Box sx={{ p:"10px 16px", bgcolor:`${color}0d`, borderBottom:`1px solid ${APP_COLORS.border}` }}>
                    <Typography sx={{ fontSize:11, color:APP_COLORS.text }}>
                        <span style={{ color, fontWeight:700 }}>💬 Strategy: </span>{current.tip}
                    </Typography>
                </Box>
                <Box sx={{ p:2 }}>
                    <CodeBlock code={current.code} copyId={`sol-${question.title}-${activeSolution}`} copiedId={copiedId} onCopy={onCopy} />
                </Box>
                <Accordion disableGutters sx={{ bgcolor:"transparent", boxShadow:"none", borderTop:`1px solid ${APP_COLORS.border}`, "&:before":{ display:"none" } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color:APP_COLORS.textMuted, fontSize:16 }} />} sx={{ px:2, py:0.5, minHeight:36 }}>
                        <Typography sx={{ fontSize:10, fontWeight:700, color:APP_COLORS.textMuted, letterSpacing:"0.06em" }}>⚖️ WHEN TO USE EACH APPROACH</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px:2, pb:2, pt:0 }}>
                        {question.solutions.map((sol,i) => (
                            <Box key={i} sx={{ display:"flex", gap:1.5, mb:i<2?1.5:0 }}>
                                <Typography sx={{ fontSize:14, minWidth:22 }}>{["🔵","🟠","🟢"][i]}</Typography>
                                <Box>
                                    <Typography sx={{ fontSize:11, fontWeight:600, color:APP_COLORS.text }}>{sol.label.replace(/^[^ ]+ /,"")}</Typography>
                                    <Typography sx={{ fontSize:10, color:APP_COLORS.textMuted, mt:0.25 }}>{sol.tip}</Typography>
                                </Box>
                            </Box>
                        ))}
                    </AccordionDetails>
                </Accordion>
            </CardContent>
        </Card>
    );
};

const InfoBar = ({ topicData }) => (
    <Box sx={{ mb:3 }}>
        <Box sx={{ display:"flex", alignItems:"center", gap:1, mb:0.5 }}>
            <Typography sx={{ fontSize:22 }}>{topicData.icon}</Typography>
            <Box>
                <Typography sx={{ fontSize:15, fontWeight:900, color:APP_COLORS.title }}>{topicData.badge}</Typography>
                <Typography sx={{ fontSize:11, color:APP_COLORS.textMuted }}>{topicData.tagline}</Typography>
            </Box>
        </Box>
        <Box sx={{ display:"inline-block", bgcolor:"rgba(0,0,0,0.4)", border:`1px solid ${APP_COLORS.border}`, borderRadius:1, px:1.5, py:0.5, mb:1.5 }}>
            <Typography component="code" sx={{ fontSize:11, color:topicData.color, fontFamily:"monospace", whiteSpace:"pre" }}>{topicData.declare}</Typography>
        </Box>
        <Box sx={{ display:"flex", flexWrap:"wrap", gap:1, alignItems:"flex-start" }}>
            {Object.entries(topicData.complexity).map(([op,val]) => (
                <ComplexityBadge key={op} operation={op} value={val} />
            ))}
            <Box sx={{ flex:1, minWidth:180, bgcolor:`${topicData.color}10`, border:`1px solid ${topicData.color}33`, borderRadius:1.5, px:1.5, py:0.75 }}>
                <Typography sx={{ fontSize:10, color:topicData.color, fontWeight:700 }}>💡 Use when:</Typography>
                <Typography sx={{ fontSize:10.5, color:APP_COLORS.text }}>{topicData.whenToUse}</Typography>
            </Box>
        </Box>
        {topicData.note && (
            <Typography sx={{ fontSize:10, color:APP_COLORS.textMuted, mt:0.75, fontStyle:"italic" }}>* {topicData.note}</Typography>
        )}
    </Box>
);

const MethodsTab = ({ topicData, copiedId, onCopy }) => {
    const [activeCategory, setActiveCategory] = useState("All");
    const categories = useMemo(() => ["All", ...new Set(topicData.methods.map(m => m.cat))], [topicData.methods]);
    const visible    = useMemo(() => activeCategory === "All" ? topicData.methods : topicData.methods.filter(m => m.cat === activeCategory), [topicData.methods, activeCategory]);

    if (topicData.methods.length === 0) {
        return (
            <Box sx={{ textAlign:"center", py:6, color:APP_COLORS.textMuted }}>
                <SchoolIcon sx={{ fontSize:40, mb:1, opacity:0.4 }} />
                <Typography sx={{ fontSize:13 }}>No individual methods — see the Practice tab for algorithm patterns.</Typography>
            </Box>
        );
    }
    return (
        <Box>
            <Box sx={{ display:"flex", flexWrap:"wrap", gap:1, mb:2.5 }}>
                {categories.map(cat => (
                    <Chip key={cat} label={cat} onClick={() => setActiveCategory(cat)} variant={activeCategory===cat?"filled":"outlined"} size="small"
                          sx={{ cursor:"pointer", fontSize:10, fontWeight:600, color:activeCategory===cat?APP_COLORS.background:APP_COLORS.textMuted, bgcolor:activeCategory===cat?topicData.color:"transparent", borderColor:activeCategory===cat?topicData.color:APP_COLORS.border, "&:hover":{ borderColor:topicData.color, color:topicData.color } }} />
                ))}
            </Box>
            <Box sx={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(260px, 1fr))", gap:1.5 }}>
                {visible.map((method,i) => (
                    <MethodCard key={method.name} method={method} color={topicData.color} cardIndex={`${topicData.badge}-${i}`} copiedId={copiedId} onCopy={onCopy} />
                ))}
            </Box>
        </Box>
    );
};

const PracticeTab = ({ topicData, copiedId, onCopy }) => {
    const [activeQuestion, setActiveQuestion] = useState(0);
    const current = topicData.questions[activeQuestion];
    return (
        <Box>
            <Box sx={{ display:"flex", flexWrap:"wrap", gap:1, mb:3 }}>
                {topicData.questions.map((q,i) => {
                    const dc = DIFFICULTY_COLOR_MAP[q.difficulty] || APP_COLORS.textMuted;
                    const isActive = activeQuestion === i;
                    return (
                        <Button key={i} onClick={() => setActiveQuestion(i)} variant={isActive?"contained":"outlined"} size="small"
                                sx={{ textTransform:"none", fontSize:11, fontWeight:700, fontFamily:"monospace", borderColor:isActive?topicData.color:APP_COLORS.border, bgcolor:isActive?`${topicData.color}22`:"transparent", color:isActive?topicData.color:APP_COLORS.textMuted, "&:hover":{ borderColor:topicData.color, bgcolor:`${topicData.color}11` }, display:"flex", flexDirection:"column", alignItems:"flex-start", px:1.5, py:0.75, lineHeight:1.4 }}>
                            <span>#{i+1} {q.title}</span>
                            <span style={{ fontSize:9, color:dc }}>● {q.difficulty}</span>
                        </Button>
                    );
                })}
            </Box>
            {current && <SolutionPanel question={current} color={topicData.color} copiedId={copiedId} onCopy={onCopy} />}
        </Box>
    );
};

const TopicContent = ({ topicData, copiedId, onCopy }) => {
    const [activeTab, setActiveTab] = useState(topicData.methods.length === 0 ? 1 : 0);
    return (
        <Box>
            <InfoBar topicData={topicData} />
            <Tabs value={activeTab} onChange={(_,v) => setActiveTab(v)} sx={{ mb:3, borderBottom:`1px solid ${APP_COLORS.border}`, "& .MuiTab-root":{ fontSize:11, fontWeight:700, color:APP_COLORS.textMuted, textTransform:"uppercase", letterSpacing:"0.05em", minHeight:40 }, "& .Mui-selected":{ color:`${topicData.color} !important` }, "& .MuiTabs-indicator":{ bgcolor:topicData.color } }}>
                <Tab label="📋 Methods" disabled={topicData.methods.length === 0} />
                <Tab label="🧩 Practice" />
            </Tabs>
            {activeTab === 0 && <MethodsTab topicData={topicData} copiedId={copiedId} onCopy={onCopy} />}
            {activeTab === 1 && <PracticeTab topicData={topicData} copiedId={copiedId} onCopy={onCopy} />}
        </Box>
    );
};

const SidebarContent = ({ activeTopicId, onSelect }) => (
    <Box sx={{ display:"flex", flexDirection:"column", height:"100%" }}>
        <Box sx={{ p:2, pb:1.5 }}>
            <Box sx={{ display:"flex", alignItems:"center", gap:1, mb:0.25 }}>
                <Box sx={{ background:"linear-gradient(135deg,#00599c,#659ad2)", borderRadius:0.75, px:0.75, py:0.3 }}>
                    <Typography sx={{ fontWeight:900, fontSize:11, color:"#fff", lineHeight:1 }}>C++</Typography>
                </Box>
                <Typography sx={{ fontWeight:900, fontSize:13, color:APP_COLORS.title, lineHeight:1.2 }}>C++</Typography>
            </Box>
            <Typography sx={{ fontSize:10, color:APP_COLORS.textMuted, pl:"38px" }}>Complete Algorithms</Typography>
        </Box>
        <Divider sx={{ borderColor:APP_COLORS.border }} />
        <List dense sx={{ flex:1, py:1, overflowY:"auto" }}>
            {ALL_TOPICS.map(topic => {
                const isActive = activeTopicId === topic.id;
                return (
                    <ListItem key={topic.id} disablePadding>
                        <ListItemButton onClick={() => onSelect(topic.id)} sx={{ borderRadius:1.5, mx:1, my:0.25, bgcolor:isActive?`${topic.data.color}22`:"transparent", borderLeft:isActive?`3px solid ${topic.data.color}`:"3px solid transparent", "&:hover":{ bgcolor:`${topic.data.color}11` }, transition:"all 0.18s", py:0.75 }}>
                            <ListItemIcon sx={{ minWidth:32, color:isActive?topic.data.color:APP_COLORS.textMuted }}>{topic.data.icon}</ListItemIcon>
                            <ListItemText primary={topic.label} primaryTypographyProps={{ fontSize:12, fontWeight:isActive?700:400, color:isActive?topic.data.color:APP_COLORS.text }} />
                            {isActive && <BookmarkIcon sx={{ fontSize:12, color:topic.data.color }} />}
                        </ListItemButton>
                    </ListItem>
                );
            })}
        </List>
        <Divider sx={{ borderColor:APP_COLORS.border }} />
        <Box sx={{ p:1.5, textAlign:"center" }}>
            <Box sx={{ display:"flex", justifyContent:"center", alignItems:"center", gap:1, mb:0.5 }}>
                <Box sx={{ background:"linear-gradient(135deg,#00599c,#659ad2)", borderRadius:0.5, px:0.5, py:0.1 }}>
                    <Typography sx={{ fontWeight:900, fontSize:9, color:"#fff" }}>C++</Typography>
                </Box>
                <Typography sx={{ fontSize:9, color:APP_COLORS.textMuted }}>C++ Algo Prep</Typography>
            </Box>
            <Typography sx={{ fontSize:9, color:APP_COLORS.textMuted }}>10 STL Structures · 6 Algorithm Patterns</Typography>
        </Box>
    </Box>
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SECTION 4 — ROOT APP
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const AppContent = () => {
    const theme    = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [activeTopicId,    setActiveTopicId]    = useState("vector");
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const [copiedId,         setCopiedId]         = useState(null);

    const activeTopic = ALL_TOPICS.find(t => t.id === activeTopicId);

    const handleTopicSelect = (id) => {
        setActiveTopicId(id);
        if (isMobile) setMobileDrawerOpen(false);
    };

    const handleCopy = (id) => {
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 1500);
    };

    const drawerContent = <SidebarContent activeTopicId={activeTopicId} onSelect={handleTopicSelect} />;

    return (
        <Box sx={{ display:"flex", minHeight:"100vh", bgcolor:APP_COLORS.background }}>
            {isMobile && (
                <AppBar position="fixed" sx={{ bgcolor:APP_COLORS.drawerBg, borderBottom:`1px solid ${APP_COLORS.border}`, zIndex:theme.zIndex.drawer+1, boxShadow:"none" }}>
                    <Toolbar variant="dense" sx={{ gap:1, minHeight:48 }}>
                        <IconButton edge="start" size="small" onClick={() => setMobileDrawerOpen(true)} sx={{ color:APP_COLORS.title }}>
                            <MenuIcon />
                        </IconButton>
                        <Typography sx={{ fontWeight:700, fontSize:13, color:APP_COLORS.title, flex:1 }}>{activeTopic?.label ?? "C++"}</Typography>
                        <Typography sx={{ fontSize:18 }}>{activeTopic?.data.icon}</Typography>
                    </Toolbar>
                </AppBar>
            )}
            {!isMobile && (
                <Drawer variant="permanent" sx={{ width:DRAWER_WIDTH, flexShrink:0, "& .MuiDrawer-paper":{ width:DRAWER_WIDTH, bgcolor:APP_COLORS.drawerBg, borderRight:`1px solid ${APP_COLORS.border}`, overflowX:"hidden" } }}>
                    {drawerContent}
                </Drawer>
            )}
            {isMobile && (
                <Drawer variant="temporary" open={mobileDrawerOpen} onClose={() => setMobileDrawerOpen(false)} ModalProps={{ keepMounted:true }}
                        sx={{ "& .MuiDrawer-paper":{ width:DRAWER_WIDTH, bgcolor:APP_COLORS.drawerBg, borderRight:`1px solid ${APP_COLORS.border}` } }}>
                    <Box sx={{ display:"flex", justifyContent:"flex-end", p:0.5 }}>
                        <IconButton size="small" onClick={() => setMobileDrawerOpen(false)} sx={{ color:APP_COLORS.textMuted }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    {drawerContent}
                </Drawer>
            )}
            <Box component="main" sx={{ flexGrow:1, mt:isMobile?"48px":0, minWidth:0, overflowX:"hidden" }}>
                <Box sx={{ maxWidth:1100, mx:"auto", p:{ xs:2, sm:3 } }}>
                    {!isMobile && (
                        <Box sx={{ display:"flex", alignItems:"center", gap:2, mb:3, pb:2, borderBottom:`1px solid ${APP_COLORS.border}` }}>
                            <Typography sx={{ fontSize:24 }}>{activeTopic?.data.icon}</Typography>
                            <Box>
                                <Box sx={{ display:"flex", alignItems:"center", gap:1.5 }}>
                                    <Box sx={{ background:"linear-gradient(135deg,#00599c,#659ad2)", borderRadius:0.5, px:0.75, py:0.2 }}>
                                        <Typography sx={{ fontWeight:900, fontSize:10, color:"#fff", lineHeight:1 }}>C++</Typography>
                                    </Box>
                                    <Typography sx={{ fontWeight:900, fontSize:18, color:APP_COLORS.title }}>{activeTopic?.label}</Typography>
                                </Box>
                                <Typography sx={{ fontSize:11, color:APP_COLORS.textMuted }}>C++ Complete Algorithms — STL Structures &amp; Patterns</Typography>
                            </Box>
                        </Box>
                    )}
                    {activeTopic && (
                        <TopicContent key={activeTopicId} topicData={activeTopic.data} copiedId={copiedId} onCopy={handleCopy} />
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default function CppCompleteAlgorithms() {
    const appTheme = useMemo(() => buildAppTheme(), []);
    return (
        <ThemeProvider theme={appTheme}>
            <CssBaseline />
            <AppContent />
        </ThemeProvider>
    );
}
